'use client';

import { Fragment, createContext, useCallback, useContext, useMemo, useRef, useState, type PropsWithChildren, type ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  ArrowRightCircle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Filter,
  FolderOpen,
  Info,
  Link as LinkIcon,
  MessageSquare,
  MinusCircle,
  PauseCircle,
  PlusCircle,
  RotateCcw,
  RefreshCcw,
  Search,
  UserCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
} from "recharts";

import {
  ChartContainer,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { RolePlaceholder } from "@/components/role-placeholder";
import { useRole } from "@/contexts/role-context";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

/**
 * Facility (Non‑Medical) — Compact App Shell
 * Reference: shadcn "A sidebar that collapses to icons" (simplified)
 * This canvas implements the requested wire‑frame for the Facility (Non‑Medical) role:
 *  - dashboards across census, requests, cases, encounters
 *  - shared window/search toolbar for time-bounded views
 *  - request creation stubs for navigation wiring
 */

const WINDOW_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_week", label: "Last Week" },
  { value: "this_week", label: "This Week" },
  { value: "last_month", label: "Last Month" },
  { value: "this_month", label: "This Month" },
  { value: "rolling_12_weeks", label: "Rolling 12 Weeks" },
  { value: "date_range", label: "Date Range" },
];

const WINDOW_LABEL_MAP = WINDOW_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "UTC",
  timeZoneName: "short",
});

const dateRangeFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

type DashboardWindowContextValue = {
  value: string;
  label: string;
  setValue: (value: string) => void;
};

const DashboardWindowContext = createContext<DashboardWindowContextValue>({
  value: "this_week",
  label: WINDOW_LABEL_MAP.this_week,
  setValue: () => undefined,
});

export function useDashboardWindow() {
  return useContext(DashboardWindowContext);
}

const formatDateTimeWithRelative = (iso: string, baseTimeMs: number = Date.now()) => {
  const date = new Date(iso);
  const formatted = dateFormatter.format(date);
  const diffMs = Math.max(0, baseTimeMs - date.getTime());
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  let relative = "just now";
  if (diffHours >= 1) {
    relative = `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  } else if (diffMinutes >= 1) {
    relative = `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  }
  return `${formatted} · ${relative}`;
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return dateFormatter.format(date);
};

const WINDOW_POINT_COUNT: Record<string, number> = {
  today: 10,
  yesterday: 10,
  last_week: 7,
  this_week: 7,
  last_month: 30,
  this_month: 30,
  rolling_12_weeks: 12,
  date_range: 12,
};

const getPointCountForWindow = (windowValue: string) => {
  return WINDOW_POINT_COUNT[windowValue] ?? 12;
};

type DateRange = {
  start: Date;
  end: Date;
};

const WEEK_OPTIONS = { weekStartsOn: 0 as const };

const getWindowDateRange = (windowValue: string, now: Date, pointCount: number): DateRange => {
  switch (windowValue) {
    case "today": {
      return { start: startOfDay(now), end: endOfDay(now) };
    }
    case "yesterday": {
      const reference = subDays(now, 1);
      return { start: startOfDay(reference), end: endOfDay(reference) };
    }
    case "last_week": {
      const reference = subWeeks(now, 1);
      return {
        start: startOfWeek(reference, WEEK_OPTIONS),
        end: endOfWeek(reference, WEEK_OPTIONS),
      };
    }
    case "this_week": {
      return {
        start: startOfWeek(now, WEEK_OPTIONS),
        end: endOfWeek(now, WEEK_OPTIONS),
      };
    }
    case "last_month": {
      const reference = subMonths(now, 1);
      return {
        start: startOfMonth(reference),
        end: endOfMonth(reference),
      };
    }
    case "this_month": {
      return {
        start: startOfMonth(now),
        end: endOfMonth(now),
      };
    }
    case "rolling_12_weeks": {
      const rangeEnd = endOfWeek(now, WEEK_OPTIONS);
      const startReference = subWeeks(rangeEnd, 11);
      return {
        start: startOfWeek(startReference, WEEK_OPTIONS),
        end: rangeEnd,
      };
    }
    case "date_range": {
      const rangeEnd = endOfDay(now);
      const span = Math.max(0, pointCount - 1);
      const rangeStart = span > 0 ? startOfDay(subDays(rangeEnd, span)) : startOfDay(rangeEnd);
      return { start: rangeStart, end: rangeEnd };
    }
    default: {
      const rangeEnd = endOfDay(now);
      const span = Math.max(0, pointCount - 1);
      const rangeStart = span > 0 ? startOfDay(subDays(rangeEnd, span)) : startOfDay(rangeEnd);
      return { start: rangeStart, end: rangeEnd };
    }
  }
};

const stringToSeed = (input: string) => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return hash >>> 0;
};

const mulberry32 = (seed: number) => {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const randomInRange = (min: number, max: number, rng: () => number) => {
  return Math.round(min + (max - min) * rng());
};

const DAY_MS = 1000 * 60 * 60 * 24;

type RoundedRectangleProps = React.ComponentProps<typeof Rectangle>;

const RoundedRectangle = (props: RoundedRectangleProps) => (
  <Rectangle {...props} radius={[8, 8, 0, 0]} />
);
const generateCensusTrendData = (
  metricKey: CensusTrendMetricKey,
  windowValue: string,
  range: DateRange,
  pointCount: number,
): CensusTrendDatum[] => {
  const count = Math.max(1, pointCount);
  const seed = stringToSeed(
    `${metricKey}-${windowValue}-${range.start.toISOString()}-${range.end.toISOString()}-${count}`,
  );
  const rng = mulberry32(seed || 1);
  const data: CensusTrendDatum[] = [];

  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const spanMs = Math.max(0, endMs - startMs);
  const stepMs = count > 1 ? spanMs / (count - 1) : 0;

  const { min, max } = CENSUS_TREND_RANGE[metricKey];

  let previous = randomInRange(min, max, rng);
  for (let index = 0; index < count; index += 1) {
    const pointTime = index === count - 1 ? endMs : startMs + stepMs * index;
    const pointDate = new Date(pointTime);
    const variance = Math.max(1, Math.round((max - min) / 6));
    const candidate = previous + randomInRange(-variance, variance, rng);
    const value = Math.max(min, Math.min(max, candidate));
    previous = value;
    data.push({
      date: pointDate.toISOString(),
      value,
    });
  }

  return data;
};

function CensusTrendChart({
  metricKey,
  metricLabel,
  windowLabel,
  dateRange,
  data,
  total,
}: {
  metricKey: CensusTrendMetricKey;
  metricLabel: string;
  windowLabel: string;
  dateRange: DateRange;
  data: CensusTrendDatum[];
  total: number;
}) {
  const color = CENSUS_TREND_COLOR_MAP[metricKey];
  const chartConfig: ChartConfig = {
    value: {
      label: metricLabel,
      color,
    },
  };

  const dateRangeLabel = useMemo(() => {
    const startLabel = dateRangeFormatter.format(dateRange.start);
    const endLabel = dateRangeFormatter.format(dateRange.end);
    return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
  }, [dateRange]);

  return (
    <Card className="py-0">
      <CardHeader className="flex flex-col items-stretch border-b !p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 pt-4 pb-3 sm:!py-0">
          <CardTitle>{metricLabel}</CardTitle>
          <CardDescription>
            Trends for {windowLabel} • {dateRangeLabel}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2 border-t px-6 py-4 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
          <span className="text-xs text-muted-foreground">Cumulative</span>
          <span className="text-lg font-bold leading-none sm:text-3xl">{total}</span>
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6">
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <RechartsTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    className="w-[180px]"
                    config={chartConfig}
                    labelFormatter={(value) =>
                      new Date(value).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    }
                  />
                }
              />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  });
                }}
              />
              <Bar
                dataKey="value"
                fill={color}
                radius={[8, 8, 0, 0]}
                name={metricLabel}
                isAnimationActive={false}
                shape={<RoundedRectangle />}
                activeBar={<RoundedRectangle />}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

function getMetricKeyFromLabel(label: string): CensusMetricKey {
  return CENSUS_METRIC_KEY_BY_LABEL.get(label) ?? "active";
}

function CensusHeader({
  mode,
  onModeChange,
  selectedMetric,
  onSelectMetric,
  trendMetric,
  onTrendMetricChange,
  onOpenTrendRangeModal,
}: {
  mode: CensusViewMode;
  onModeChange: (mode: CensusViewMode) => void;
  selectedMetric: string;
  onSelectMetric: (label: string) => void;
  trendMetric: CensusTrendMetricKey;
  onTrendMetricChange: (metric: CensusTrendMetricKey) => void;
  onOpenTrendRangeModal: () => void;
}) {
  const isTrends = mode === "trends";

  return (
    <div className="space-y-4">
      <Card className="py-0">
        <CardHeader className="flex flex-col gap-4 px-6 py-4 md:flex md:flex-row md:items-start md:justify-between md:gap-8">
          <div className="space-y-1 md:flex-none md:min-w-[16rem] md:max-w-[22rem]">
            <CardTitle className="text-2xl font-semibold text-foreground">Census & Schedule</CardTitle>
            <CardDescription>Snapshot of patient volume and scheduling activity.</CardDescription>
          </div>
          <div className="flex w-full flex-wrap justify-center gap-3 sm:justify-end md:flex-1 md:justify-end">
            {CENSUS_METRICS.map((metric) => {
              const isActive = selectedMetric === metric.label;
              const MetricIcon = CENSUS_METRIC_ICONS[metric.key];
              const metricTitle =
                metric.key === "scheduled"
                  ? "Visits\nScheduled"
                  : metric.key === "completed"
                    ? "Visits\nCompleted"
                    : metric.key === "approved"
                      ? "Approved\nCensus"
                      : metric.label;
              return (
                <Tooltip key={metric.key}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className={cn(
                        "flex h-auto min-w-[6.5rem] flex-col gap-2 rounded-lg border bg-muted/30 px-2.5 py-2 text-left shadow-sm transition-all duration-150 hover:translate-y-[-1px] sm:min-w-[7.5rem]",
                        isActive
                          ? "border-primary text-foreground shadow-md"
                          : "border-border/70 text-foreground hover:border-primary/50 hover:bg-muted/50",
                      )}
                      onClick={() => onSelectMetric(metric.label)}
                    >
                      <span
                        className={cn(
                          "line-clamp-2 h-[36px] whitespace-pre-line text-left text-xs font-medium uppercase tracking-wide leading-tight",
                          isActive ? "text-primary" : "text-muted-foreground",
                        )}
                      >
                        {metricTitle}
                      </span>
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            "flex h-9 w-9 flex-none items-center justify-center rounded-md bg-background/60",
                            isActive ? "text-primary" : "text-muted-foreground",
                          )}
                        >
                          <MetricIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <span className="text-left text-2xl font-semibold text-foreground">{metric.value}</span>
                      </div>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-xs text-xs leading-relaxed">
                    <p className="font-medium text-foreground">{metric.delta}</p>
                    <p className="text-muted-foreground">{metric.description}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </CardHeader>
      </Card>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={mode} onValueChange={(value) => onModeChange(value as CensusViewMode)} className="w-full sm:w-auto">
          <TabsList className="grid w-full grid-cols-3 sm:w-[360px]">
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="tabular">Tabular</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>
        </Tabs>
        {isTrends ? (
          <div className="flex flex-wrap items-center gap-2">
            <Select value={trendMetric} onValueChange={(value) => onTrendMetricChange(value as CensusTrendMetricKey)}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Select metric" />
              </SelectTrigger>
              <SelectContent>
                {CENSUS_TREND_METRICS.map((metric) => (
                  <SelectItem key={metric.key} value={metric.key}>
                    {metric.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={onOpenTrendRangeModal}>
              Adjust range
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CensusTabularView({
  selectedMetric,
  onSelectMetric,
}: {
  selectedMetric: string;
  onSelectMetric: (label: string) => void;
}) {
  const metricKey = getMetricKeyFromLabel(selectedMetric);
  const rows = useMemo(() => CENSUS_TABLE_ROWS.filter((row) => row.metricKey === metricKey), [metricKey]);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-base font-semibold text-foreground">{selectedMetric}</CardTitle>
          <CardDescription>Recent activity associated with this metric.</CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {CENSUS_METRICS.map((metric) => (
            <Button
              key={metric.key}
              size="sm"
              variant={metric.label === selectedMetric ? "secondary" : "outline"}
              onClick={() => onSelectMetric(metric.label)}
            >
              {metric.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="px-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr className="border-b">
                <th className="px-4 py-2 text-left font-medium">ID</th>
                <th className="px-4 py-2 text-left font-medium">Requested</th>
                <th className="px-4 py-2 text-left font-medium">Approved</th>
                <th className="px-4 py-2 text-left font-medium">Patient</th>
                <th className="px-4 py-2 text-left font-medium">DOB</th>
                <th className="px-4 py-2 text-left font-medium">Primary</th>
                <th className="px-4 py-2 text-left font-medium">Secondary</th>
                <th className="px-4 py-2 text-left font-medium">Last Visit</th>
                <th className="px-4 py-2 text-left font-medium">Next Visit</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-xs text-muted-foreground">
                    No records for this metric during the selected period.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-b-0">
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{row.id}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{formatDateTime(row.requestedAt)}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{formatDateTime(row.approvedAt)}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{row.name}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{row.dob}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{row.primaryProvider}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{row.secondaryProvider ?? "—"}</td>
                    <td className="px-4 py-2 text-sm text-foreground">
                      {row.lastVisitAt ? format(new Date(row.lastVisitAt), "MMM d, yyyy") : "—"}
                    </td>
                    <td className="px-4 py-2 text-sm text-foreground">
                      {row.nextVisitAt ? format(new Date(row.nextVisitAt), "MMM d, yyyy") : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function CensusContent({
  mode,
  selectedMetric,
  onSelectMetric,
  selectedTrendMetric,
  trendDateRange,
}: {
  mode: CensusViewMode;
  selectedMetric: string;
  onSelectMetric: (label: string) => void;
  selectedTrendMetric: CensusTrendMetricKey;
  trendDateRange: ActionDateRangeValue;
}) {
  const { value: windowValue, label: windowLabel } = useDashboardWindow();
  const now = useMemo(() => new Date(), []);

  if (mode === "schedule") {
    return <CalendarMock />;
  }

  if (mode === "tabular") {
    return <CensusTabularView selectedMetric={selectedMetric} onSelectMetric={onSelectMetric} />;
  }

  let pointCount = getPointCountForWindow(windowValue);
  let range: DateRange;
  if (windowValue === "date_range") {
    const start = startOfDay(trendDateRange.start);
    const end = endOfDay(trendDateRange.end);
    const diffDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / DAY_MS));
    pointCount = Math.min(60, diffDays + 1 || pointCount);
    range = { start, end };
  } else {
    range = getWindowDateRange(windowValue, now, pointCount);
  }

  const data = generateCensusTrendData(selectedTrendMetric, windowValue, range, pointCount);
  const total = data.reduce((sum, datum) => sum + datum.value, 0);

  return (
    <CensusTrendChart
      metricKey={selectedTrendMetric}
      metricLabel={CENSUS_TREND_LABEL_MAP[selectedTrendMetric]}
      windowLabel={windowLabel}
      dateRange={range}
      data={data}
      total={total}
    />
  );
}

type RequestLifecycleCategory = "Open" | "Closed";

type RequestQuickFilterKey =
  | "draft"
  | "submitted"
  | "approved"
  | "returned"
  | "hold"
  | "ineligible"
  | "completed"
  | "notCompleted"
  | "canceled";

type RequestCardTabKey = "overview" | "timeline" | "linked";

type RequestParticipant = {
  id: string;
  name: string;
  role?: string;
};

type RequestCardProperty = {
  label: string;
  value: string;
};

type RequestCareAssignment = {
  occurredAt: string;
  participant: RequestParticipant;
};

type RequestCareTeam = {
  primary?: RequestCareAssignment;
  secondary?: RequestCareAssignment;
};

type RequestLinkedItem = {
  id: string;
  title: string;
  type: string;
};

type RequestLinkedCollection = {
  count: number;
  items: RequestLinkedItem[];
};

type RequestLifecycleState = {
  category: RequestLifecycleCategory;
  path: string[];
  occurredAt: string;
};

type RequestCard = {
  id: string;
  patient: string;
  patientDob?: string;
  typePath: string[];
  requested: string;
  quickFilterKey: RequestQuickFilterKey;
  lifecycle: RequestLifecycleState;
  initiatedBy: RequestParticipant;
  initiatedAt: string;
  assignee: RequestParticipant;
  lastEvent: {
    title: string;
    occurredAt: string;
  };
  flags?: string[];
  description: string;
  properties?: RequestCardProperty[];
  linkedConversations: RequestLinkedCollection;
  linkedActions: RequestLinkedCollection;
  careTeam?: RequestCareTeam;
};

type ActionDateRangeValue = {
  start: Date;
  end: Date;
};

type ActionDateRangePreset = {
  label: string;
  description?: string;
  getRange: (now: Date) => ActionDateRangeValue;
};

type CensusMetricKey = "active" | "approved" | "pending" | "scheduled" | "completed";

type CensusTrendMetricKey = CensusMetricKey;

type CensusMetricDefinition = {
  key: CensusMetricKey;
  label: string;
  value: number;
  delta: string;
  description: string;
};

const CENSUS_METRICS: CensusMetricDefinition[] = [
  {
    key: "active",
    label: "Active Census",
    value: 48,
    delta: "+3 vs last week",
    description: "Patients under care of __COMPANY__, have been seen or are scheduled to be seen.",
  },
  {
    key: "approved",
    label: "Approved Census",
    value: 32,
    delta: "+1 vs yesterday",
    description: "Approved Patient Referral Requests, have not been seen.",
  },
  {
    key: "pending",
    label: "Pending Census",
    value: 6,
    delta: "-2 vs last week",
    description: "Patient Referral Requests submitted and pending approval.",
  },
  {
    key: "scheduled",
    label: "Visits Scheduled",
    value: 14,
    delta: "+4 vs yesterday",
    description: "Approved referrals with confirmed visit scheduling.",
  },
  {
    key: "completed",
    label: "Visits Completed",
    value: 11,
    delta: "+2 vs yesterday",
    description: "Visits completed over the period shown on the calendar below.",
  },
];

const CENSUS_METRIC_ICONS: Record<CensusMetricKey, LucideIcon> = {
  active: Users,
  approved: UserCheck,
  pending: Clock3,
  scheduled: CalendarClock,
  completed: CheckCircle2,
};

const CENSUS_METRIC_KEY_BY_LABEL = new Map<string, CensusMetricKey>(
  CENSUS_METRICS.map((metric) => [metric.label, metric.key]),
);

const CENSUS_TREND_METRICS: { key: CensusTrendMetricKey; label: string }[] = [
  { key: "active", label: "Active Census" },
  { key: "approved", label: "Approved Census" },
  { key: "pending", label: "Pending Census" },
  { key: "scheduled", label: "Visits Scheduled" },
  { key: "completed", label: "Visits Completed" },
];

const CENSUS_TREND_LABEL_MAP: Record<CensusTrendMetricKey, string> = Object.fromEntries(
  CENSUS_TREND_METRICS.map((metric) => [metric.key, metric.label]),
) as Record<CensusTrendMetricKey, string>;

const CENSUS_TREND_COLOR_MAP: Record<CensusTrendMetricKey, string> = {
  active: "hsl(var(--chart-1))",
  approved: "hsl(var(--chart-2))",
  pending: "hsl(var(--chart-3))",
  scheduled: "hsl(var(--chart-4))",
  completed: "hsl(var(--chart-5))",
};

const CENSUS_TREND_RANGE: Record<CensusTrendMetricKey, { min: number; max: number }> = {
  active: { min: 30, max: 60 },
  approved: { min: 18, max: 36 },
  pending: { min: 4, max: 12 },
  scheduled: { min: 8, max: 20 },
  completed: { min: 6, max: 18 },
};

type CensusTableRow = {
  id: string;
  metricKey: CensusMetricKey;
  requestedAt: string;
  approvedAt?: string;
  name: string;
  dob: string;
  primaryProvider: string;
  secondaryProvider?: string;
  lastVisitAt?: string;
  nextVisitAt?: string;
};

const getCurrentMonthRange = (now: Date): ActionDateRangeValue => {
  const start = startOfMonth(now);
  const end = endOfMonth(now);
  return { start, end };
};

const formatActionDateRange = (value: ActionDateRangeValue) => {
  const startLabel = format(value.start, "MMM d");
  const endLabel = format(value.end, "MMM d");
  if (startLabel === endLabel) {
    return startLabel;
  }
  const startYear = value.start.getFullYear();
  const endYear = value.end.getFullYear();
  const includeYear = startYear !== endYear;
  const startFormatted = includeYear ? format(value.start, "MMM d, yyyy") : startLabel;
  const endFormatted = format(value.end, "MMM d, yyyy");
  return `${startFormatted} – ${endFormatted}`;
};

const ACTION_DATE_RANGE_PRESETS: ActionDateRangePreset[] = [
  {
    label: "Current Month",
    description: "All activity for the current calendar month.",
    getRange: (now) => getCurrentMonthRange(now),
  },
  {
    label: "Previous Month",
    description: "Everything that occurred last month.",
    getRange: (now) => ({
      start: startOfMonth(subMonths(now, 1)),
      end: endOfMonth(subMonths(now, 1)),
    }),
  },
  {
    label: "Last 30 Days",
    description: "Rolling 30 day window ending today.",
    getRange: (now) => ({
      start: startOfDay(subDays(now, 29)),
      end: endOfDay(now),
    }),
  },
];

const CENSUS_TABLE_ROWS: CensusTableRow[] = [
  {
    id: "CNS-101",
    metricKey: "active",
    requestedAt: "2025-09-22T14:30:00Z",
    approvedAt: "2025-09-23T09:12:00Z",
    name: "Jordan Chu",
    dob: "1990-04-11",
    primaryProvider: "Dr. Priya Patel",
    secondaryProvider: "NP Leah Ortiz",
    lastVisitAt: "2025-09-24T15:00:00Z",
    nextVisitAt: "2025-10-02T13:30:00Z",
  },
  {
    id: "CNS-102",
    metricKey: "active",
    requestedAt: "2025-09-21T10:45:00Z",
    approvedAt: "2025-09-22T08:05:00Z",
    name: "Alexis Monroe",
    dob: "1987-01-19",
    primaryProvider: "Dr. Cameron Blake",
    lastVisitAt: "2025-09-25T09:00:00Z",
    nextVisitAt: "2025-10-03T09:30:00Z",
  },
  {
    id: "CNS-203",
    metricKey: "approved",
    requestedAt: "2025-09-20T07:25:00Z",
    approvedAt: "2025-09-20T16:20:00Z",
    name: "Theo Vargas",
    dob: "1976-07-09",
    primaryProvider: "NP George Lin",
    secondaryProvider: "PA Jordan Chu",
    lastVisitAt: "2025-09-18T11:00:00Z",
    nextVisitAt: "2025-09-29T14:00:00Z",
  },
  {
    id: "CNS-305",
    metricKey: "pending",
    requestedAt: "2025-09-23T12:05:00Z",
    name: "Camila Reyes",
    dob: "1998-02-22",
    primaryProvider: "Dr. Elise Shaw",
    nextVisitAt: "2025-09-28T10:15:00Z",
  },
  {
    id: "CNS-401",
    metricKey: "scheduled",
    requestedAt: "2025-09-23T09:15:00Z",
    approvedAt: "2025-09-23T11:40:00Z",
    name: "Miles Everett",
    dob: "1969-11-04",
    primaryProvider: "Dr. Hannah Cho",
    lastVisitAt: "2025-09-21T16:30:00Z",
    nextVisitAt: "2025-09-25T11:45:00Z",
  },
  {
    id: "CNS-402",
    metricKey: "scheduled",
    requestedAt: "2025-09-23T08:55:00Z",
    approvedAt: "2025-09-23T12:10:00Z",
    name: "Nia Coleman",
    dob: "2001-05-14",
    primaryProvider: "NP Erin Wake",
    secondaryProvider: "PA Jordan Chu",
    lastVisitAt: "2025-09-19T14:20:00Z",
    nextVisitAt: "2025-09-27T09:00:00Z",
  },
  {
    id: "CNS-510",
    metricKey: "completed",
    requestedAt: "2025-09-18T10:10:00Z",
    approvedAt: "2025-09-18T13:45:00Z",
    name: "Harper Singh",
    dob: "1984-03-12",
    primaryProvider: "Dr. Priya Patel",
    secondaryProvider: "RN Mateo Cruz",
    lastVisitAt: "2025-09-24T12:00:00Z",
    nextVisitAt: "2025-10-08T12:30:00Z",
  },
  {
    id: "CNS-511",
    metricKey: "completed",
    requestedAt: "2025-09-17T09:40:00Z",
    approvedAt: "2025-09-17T15:00:00Z",
    name: "Amelia Brooks",
    dob: "1992-11-22",
    primaryProvider: "Dr. Hannah Cho",
    lastVisitAt: "2025-09-23T08:30:00Z",
    nextVisitAt: "2025-10-09T10:00:00Z",
  },
];

type CensusViewMode = "schedule" | "tabular" | "trends";

type CensusTrendDatum = {
  date: string;
  value: number;
};

const REQUEST_CARD_FIXTURES: readonly RequestCard[] = [
  {
    id: "REQ-2025-0001",
    patient: "Doe, John",
    patientDob: "1985-02-14",
    typePath: ["Patient", "Referral"],
    requested: "Psychiatric Intake Evaluation",
    quickFilterKey: "draft",
    lifecycle: {
      category: "Open",
      path: ["Open", "Draft"],
      occurredAt: "2025-09-23T13:45:00Z",
    },
    initiatedBy: { id: "usr-jcarter", name: "Julia Carter", role: "Facility User" },
    initiatedAt: "2025-09-23T09:12:00Z",
    assignee: { id: "usr-amarsh", name: "Avery Marsh", role: "MSP Intake" },
    lastEvent: { title: "Draft Saved", occurredAt: "2025-09-23T13:45:00Z" },
    flags: ["Pending Payer Information"],
    description: "New referral intake created from faxed facesheet; awaiting insurance card upload before submission.",
    properties: [
      { label: "Referred Patient", value: "Doe, John" },
      { label: "Primary Provider", value: "Dr. Asha Patel" },
    ],
    linkedConversations: {
      count: 2,
      items: [
        { id: "conv-401", title: "Referring clinic intake call", type: "Call Log" },
        { id: "conv-422", title: "Family follow-up", type: "Secure Message" },
      ],
    },
    linkedActions: {
      count: 5,
      items: [
        { id: "act-901", title: "Insurance eligibility check", type: "Task" },
        { id: "act-907", title: "Assign clinician reviewer", type: "Workflow" },
        { id: "act-911", title: "Collect consent forms", type: "Task" },
        { id: "item-901", title: "Patient › Doe, John", type: "Patient" },
        { id: "item-902", title: "Patient › Doe, John › Case › CAS-8840", type: "Case" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-23T12:00:00Z",
        participant: { id: "prov-ashapatel", name: "Dr. Asha Patel", role: "Psychiatrist" },
      },
      secondary: {
        occurredAt: "2025-09-23T12:05:00Z",
        participant: { id: "prov-owenhall", name: "RN Owen Hall", role: "Intake Nurse" },
      },
    },
  },
  {
    id: "REQ-2025-0002",
    patient: "Nguyen, Lila",
    patientDob: "1991-07-08",
    typePath: ["Patient", "Referral"],
    requested: "Partial Hospitalization Program Transfer",
    quickFilterKey: "submitted",
    lifecycle: {
      category: "Open",
      path: ["Open", "Submitted"],
      occurredAt: "2025-09-22T15:20:00Z",
    },
    initiatedBy: { id: "usr-nbrooks", name: "Nina Brooks", role: "Utilization Review" },
    initiatedAt: "2025-09-22T14:05:00Z",
    assignee: { id: "usr-hcosta", name: "Hector Costa", role: "Clinical Reviewer" },
    lastEvent: { title: "Submitted to MSP Intake", occurredAt: "2025-09-22T15:20:00Z" },
    flags: ["Not Scheduled"],
    description: "Existing patient transfer request submitted after family meeting; awaiting clinical routing for program match.",
    properties: [
      { label: "Patient", value: "Nguyen, Lila" },
      { label: "Referencing Case", value: "CASE-8831" },
      { label: "Requested Program", value: "PHP - Mesa Campus" },
    ],
    linkedConversations: {
      count: 1,
      items: [{ id: "conv-512", title: "Family conference summary", type: "Team Message" }],
    },
    linkedActions: {
      count: 4,
      items: [
        { id: "act-932", title: "Benefit verification follow-up", type: "Task" },
        { id: "act-934", title: "Clinical packet upload", type: "Checklist" },
        { id: "item-934", title: "Patient › Nguyen, Lila", type: "Patient" },
        { id: "item-935", title: "Patient › Nguyen, Lila › Referral › PHP - Mesa Campus", type: "Referral" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-22T16:10:00Z",
        participant: { id: "prov-priyapatel", name: "Dr. Priya Patel", role: "Program Director" },
      },
      secondary: {
        occurredAt: "2025-09-22T16:45:00Z",
        participant: { id: "prov-georgelin", name: "NP George Lin", role: "Utilization NP" },
      },
    },
  },
  {
    id: "REQ-2025-0003",
    patient: "Sanders, Malik",
    patientDob: "1984-11-05",
    typePath: ["Patient", "Referral"],
    requested: "Residential Re-Assessment",
    quickFilterKey: "submitted",
    lifecycle: {
      category: "Open",
      path: ["Open", "Submitted", "Pending Approval"],
      occurredAt: "2025-09-21T18:10:00Z",
    },
    initiatedBy: { id: "usr-dflores", name: "Dani Flores", role: "Case Manager" },
    initiatedAt: "2025-09-21T17:32:00Z",
    assignee: { id: "usr-hcosta", name: "Hector Costa", role: "Clinical Reviewer" },
    lastEvent: { title: "Awaiting payer medical director review", occurredAt: "2025-09-21T18:10:00Z" },
    flags: ["Payer Follow-up Scheduled"],
    description: "Re-assessment package submitted; payer SLA window closes in 12 business hours.",
    properties: [
      { label: "Patient", value: "Sanders, Malik" },
      { label: "Current Level of Care", value: "Residential" },
      { label: "Requested Level of Care", value: "Step-up Residential" },
    ],
    linkedConversations: {
      count: 2,
      items: [
        { id: "conv-545", title: "Payer clinical reviewer call", type: "Call Log" },
        { id: "conv-551", title: "Family expectations email", type: "Secure Message" },
      ],
    },
    linkedActions: {
      count: 4,
      items: [
        { id: "act-941", title: "Upload updated treatment plan", type: "Task" },
        { id: "act-946", title: "Schedule psychiatrist consult", type: "Workflow" },
        { id: "item-941", title: "Patient › Sanders, Malik", type: "Patient" },
        { id: "item-942", title: "Patient › Sanders, Malik › Payer › United Healthcare", type: "Payer" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-21T19:00:00Z",
        participant: { id: "prov-cameronblake", name: "Dr. Cameron Blake", role: "Behavioral Psychiatrist" },
      },
      secondary: {
        occurredAt: "2025-09-21T19:20:00Z",
        participant: { id: "prov-mateocruz", name: "RN Mateo Cruz", role: "Clinical Liaison" },
      },
    },
  },
  {
    id: "REQ-2025-0004",
    patient: "Carr, Elise",
    patientDob: "1979-11-03",
    typePath: ["Patient", "Referral"],
    requested: "Community Psychiatry Follow-up",
    quickFilterKey: "approved",
    lifecycle: {
      category: "Open",
      path: ["Open", "Approved"],
      occurredAt: "2025-09-21T09:00:00Z",
    },
    initiatedBy: { id: "usr-amarsh", name: "Avery Marsh", role: "MSP Intake" },
    initiatedAt: "2025-09-20T12:48:00Z",
    assignee: { id: "usr-nbrooks", name: "Nina Brooks", role: "Utilization Review" },
    lastEvent: { title: "Approved by payer clinical lead", occurredAt: "2025-09-21T09:00:00Z" },
    flags: ["Not Scheduled"],
    description: "Outbound referral approved; coordination with community provider in progress.",
    properties: [
      { label: "Patient", value: "Carr, Elise" },
      { label: "Receiving Organization", value: "Valley Behavioral Group" },
      { label: "Scheduled Follow-up", value: "Pending" },
    ],
    linkedConversations: {
      count: 1,
      items: [{ id: "conv-563", title: "Payer approval confirmation", type: "Secure Message" }],
    },
    linkedActions: {
      count: 3,
      items: [
        { id: "act-952", title: "Coordinate first visit date", type: "Task" },
        { id: "item-952", title: "Patient › Carr, Elise", type: "Patient" },
        { id: "item-953", title: "Patient › Carr, Elise › Receiving Facility › Valley Behavioral Group", type: "Facility" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-21T09:05:00Z",
        participant: { id: "prov-hannahcho", name: "Dr. Hannah Cho", role: "Attending Psychiatrist" },
      },
      secondary: {
        occurredAt: "2025-09-21T09:30:00Z",
        participant: { id: "prov-mateocruz", name: "RN Mateo Cruz", role: "Outreach Nurse" },
      },
    },
  },
  {
    id: "REQ-2025-0005",
    patient: "Diaz, Ramon",
    patientDob: "1965-06-17",
    typePath: ["Patient", "Records"],
    requested: "Psychiatry Progress Notes",
    quickFilterKey: "returned",
    lifecycle: {
      category: "Open",
      path: ["Open", "Returned", "To Initiator", "More Information"],
      occurredAt: "2025-09-20T11:32:00Z",
    },
    initiatedBy: { id: "usr-lporter", name: "Leah Porter", role: "Medical Records" },
    initiatedAt: "2025-09-19T16:04:00Z",
    assignee: { id: "usr-lporter", name: "Leah Porter", role: "Medical Records" },
    lastEvent: { title: "Returned for missing consent", occurredAt: "2025-09-20T11:32:00Z" },
    flags: ["Missing Consent", "Pending Payer Information"],
    description: "Payer requested additional consent documentation before records release.",
    properties: [
      { label: "Patient", value: "Diaz, Ramon" },
      { label: "Payer", value: "AZ HealthChoice" },
      { label: "Records Window", value: "Jun 1 – Sep 15, 2025" },
    ],
    linkedConversations: {
      count: 2,
      items: [
        { id: "conv-577", title: "Payer secure message", type: "Secure Message" },
        { id: "conv-579", title: "Clinical documentation team chat", type: "Team Message" },
      ],
    },
    linkedActions: {
      count: 4,
      items: [
        { id: "act-963", title: "Collect updated consent", type: "Task" },
        { id: "act-965", title: "Re-upload progress notes", type: "Task" },
        { id: "item-963", title: "Patient › Diaz, Ramon", type: "Patient" },
        { id: "item-964", title: "Patient › Diaz, Ramon › Payer › AZ HealthChoice", type: "Payer" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-19T18:15:00Z",
        participant: { id: "prov-hannahcho", name: "Dr. Hannah Cho", role: "Psychiatrist" },
      },
      secondary: {
        occurredAt: "2025-09-19T18:40:00Z",
        participant: { id: "prov-leahporter", name: "Leah Porter", role: "Records Specialist" },
      },
    },
  },
  {
    id: "REQ-2025-0006",
    patient: "Coleman, Nia",
    patientDob: "1999-03-28",
    typePath: ["Patient", "Records"],
    requested: "Quarterly Utilization Packet",
    quickFilterKey: "hold",
    lifecycle: {
      category: "Open",
      path: ["Open", "Hold", "Administrative"],
      occurredAt: "2025-09-19T10:05:00Z",
    },
    initiatedBy: { id: "usr-lporter", name: "Leah Porter", role: "Medical Records" },
    initiatedAt: "2025-09-18T08:40:00Z",
    assignee: { id: "usr-jcarter", name: "Julia Carter", role: "Facility User" },
    lastEvent: { title: "Hold applied pending contract update", occurredAt: "2025-09-19T10:05:00Z" },
    flags: ["Contract Exception"],
    description: "Batch payer request paused while new contract terms are finalized.",
    properties: [
      { label: "Patient", value: "Coleman, Nia" },
      { label: "Payer", value: "Sunrise Health Plan" },
      { label: "Batch ID", value: "BATCH-2045" },
    ],
    linkedConversations: {
      count: 1,
      items: [{ id: "conv-584", title: "Contracting liaison note", type: "Internal Note" }],
    },
    linkedActions: {
      count: 4,
      items: [
        { id: "act-972", title: "Upload revised payer addendum", type: "Task" },
        { id: "act-973", title: "Re-run document merge", type: "Automation" },
        { id: "item-972", title: "Patient › Coleman, Nia", type: "Patient" },
        { id: "item-973", title: "Patient › Coleman, Nia › Batch Request › BATCH-2045", type: "Batch" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-18T11:30:00Z",
        participant: { id: "prov-owenhall", name: "RN Owen Hall", role: "Records Nurse" },
      },
      secondary: undefined,
    },
  },
  {
    id: "REQ-2025-0007",
    patient: "Everett, Miles",
    patientDob: "1975-12-05",
    typePath: ["Patient", "Visit"],
    requested: "Emergency Provider Visit",
    quickFilterKey: "hold",
    lifecycle: {
      category: "Open",
      path: ["Open", "Hold", "Waiting Information"],
      occurredAt: "2025-09-23T10:40:00Z",
    },
    initiatedBy: { id: "usr-kklein", name: "Kara Klein", role: "Scheduling" },
    initiatedAt: "2025-09-23T09:55:00Z",
    assignee: { id: "usr-kklein", name: "Kara Klein", role: "Scheduling" },
    lastEvent: { title: "Awaiting family confirmation of availability", occurredAt: "2025-09-23T10:40:00Z" },
    flags: ["Family Follow-up", "Not Scheduled"],
    description: "Out-of-cycle visit created for symptom escalation; pending family confirmation of visit window.",
    properties: [
      { label: "Patient", value: "Everett, Miles" },
      { label: "Preferred Clinician", value: "NP Erin Wake" },
      { label: "Visit Window", value: "Sep 25 – Sep 26, 2025" },
    ],
    linkedConversations: {
      count: 3,
      items: [
        { id: "conv-601", title: "Family voicemail transcription", type: "Call Log" },
        { id: "conv-603", title: "Provider availability update", type: "Team Message" },
        { id: "conv-606", title: "Transportation inquiry", type: "Secure Message" },
      ],
    },
    linkedActions: {
      count: 4,
      items: [
        { id: "act-981", title: "Confirm transport vendor", type: "Task" },
        { id: "act-982", title: "Update visit checklist", type: "Checklist" },
        { id: "item-981", title: "Patient › Everett, Miles", type: "Patient" },
        { id: "item-982", title: "Patient › Everett, Miles › Visit › Sept 2025 Out-of-Cycle", type: "Visit" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-23T11:05:00Z",
        participant: { id: "prov-erinwake", name: "NP Erin Wake", role: "On-call Provider" },
      },
      secondary: {
        occurredAt: "2025-09-23T11:20:00Z",
        participant: { id: "prov-rosamendoza", name: "RN Rosa Mendoza", role: "Crisis Nurse" },
      },
    },
  },
  {
    id: "REQ-2025-0008",
    patient: "Reyes, Camila",
    patientDob: "1990-01-19",
    typePath: ["Patient", "Status Update"],
    requested: "Level of Care Reassessment",
    quickFilterKey: "ineligible",
    lifecycle: {
      category: "Closed",
      path: ["Closed", "Ineligible"],
      occurredAt: "2025-09-18T16:22:00Z",
    },
    initiatedBy: { id: "usr-dflores", name: "Dani Flores", role: "Case Manager" },
    initiatedAt: "2025-09-17T11:02:00Z",
    assignee: { id: "usr-hcosta", name: "Hector Costa", role: "Clinical Reviewer" },
    lastEvent: { title: "Marked ineligible by payer rules engine", occurredAt: "2025-09-18T16:22:00Z" },
    description: "Payer automation flagged reassessment as outside benefit window; closed as ineligible.",
    properties: [
      { label: "Patient", value: "Reyes, Camila" },
      { label: "Benefit Window Start", value: "Oct 1, 2025" },
      { label: "Reason", value: "Benefit Frequency Limit" },
    ],
    linkedConversations: {
      count: 1,
      items: [{ id: "conv-612", title: "Payer denial notice", type: "Secure Message" }],
    },
    linkedActions: {
      count: 3,
      items: [
        { id: "act-988", title: "Notify facility leadership", type: "Task" },
        { id: "item-988", title: "Patient › Reyes, Camila", type: "Patient" },
        { id: "item-989", title: "Patient › Reyes, Camila › Payer › United Healthcare", type: "Payer" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-18T14:15:00Z",
        participant: { id: "prov-georgelin", name: "NP George Lin", role: "Clinical Reviewer" },
      },
      secondary: undefined,
    },
  },
  {
    id: "REQ-2025-0009",
    patient: "Brooks, Nina",
    patientDob: "1988-09-14",
    typePath: ["Patient", "Discharge"],
    requested: "Program Discharge Summary",
    quickFilterKey: "completed",
    lifecycle: {
      category: "Closed",
      path: ["Closed", "Completed", "As Requested"],
      occurredAt: "2025-09-20T20:40:00Z",
    },
    initiatedBy: { id: "usr-nbrooks", name: "Nina Brooks", role: "Utilization Review" },
    initiatedAt: "2025-09-19T08:12:00Z",
    assignee: { id: "usr-jcarter", name: "Julia Carter", role: "Facility User" },
    lastEvent: { title: "Discharge package sent to payer", occurredAt: "2025-09-20T20:40:00Z" },
    flags: ["SLA Met"],
    description: "Discharge summary compiled and transmitted; closed as completed without condition.",
    properties: [
      { label: "Patient", value: "Brooks, Nina" },
      { label: "Discharge Date", value: "Sep 20, 2025" },
      { label: "Next Steps", value: "Aftercare team notified" },
    ],
    linkedConversations: {
      count: 2,
      items: [
        { id: "conv-621", title: "Aftercare coordination", type: "Team Message" },
        { id: "conv-622", title: "Payer receipt confirmation", type: "Secure Message" },
      ],
    },
    linkedActions: {
      count: 3,
      items: [
        { id: "act-991", title: "Archive discharge documents", type: "Task" },
        { id: "item-991", title: "Patient › Brooks, Nina", type: "Patient" },
        { id: "item-992", title: "Patient › Brooks, Nina › Discharge › Sept 2025", type: "Discharge" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-20T18:05:00Z",
        participant: { id: "prov-priyapatel", name: "Dr. Priya Patel", role: "Attending Psychiatrist" },
      },
      secondary: {
        occurredAt: "2025-09-20T18:30:00Z",
        participant: { id: "prov-mateocruz", name: "RN Mateo Cruz", role: "Discharge Nurse" },
      },
    },
  },
  {
    id: "REQ-2025-0010",
    patient: "Diaz, Ramon",
    patientDob: "1965-06-17",
    typePath: ["Patient", "Visit"],
    requested: "Medication Management Follow-up",
    quickFilterKey: "completed",
    lifecycle: {
      category: "Closed",
      path: ["Closed", "Completed", "With Conditions"],
      occurredAt: "2025-09-19T12:18:00Z",
    },
    initiatedBy: { id: "usr-kklein", name: "Kara Klein", role: "Scheduling" },
    initiatedAt: "2025-09-18T09:48:00Z",
    assignee: { id: "usr-amarsh", name: "Avery Marsh", role: "MSP Intake" },
    lastEvent: { title: "Completed pending lab follow-up", occurredAt: "2025-09-19T12:18:00Z" },
    flags: ["Follow-up Labs Required"],
    description: "Visit fulfilled with condition that lab panel be repeated within 7 days.",
    properties: [
      { label: "Patient", value: "Diaz, Ramon" },
      { label: "Visit Date", value: "Sep 19, 2025" },
      { label: "Condition", value: "Repeat CMP by Sep 26" },
    ],
    linkedConversations: {
      count: 2,
      items: [
        { id: "conv-631", title: "Provider summary note", type: "Secure Message" },
        { id: "conv-633", title: "Family follow-up instructions", type: "Call Log" },
      ],
    },
    linkedActions: {
      count: 4,
      items: [
        { id: "act-996", title: "Order lab panel", type: "Task" },
        { id: "act-998", title: "Confirm lab results", type: "Task" },
        { id: "item-996", title: "Patient › Diaz, Ramon", type: "Patient" },
        { id: "item-997", title: "Patient › Diaz, Ramon › Medication › Metabolic Panel Follow-up", type: "Order" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-18T14:20:00Z",
        participant: { id: "prov-miralopez", name: "NP Mira Lopez", role: "Medication NP" },
      },
      secondary: {
        occurredAt: "2025-09-18T14:40:00Z",
        participant: { id: "prov-leahporter", name: "Leah Porter", role: "Follow-up Coordinator" },
      },
    },
  },
  {
    id: "REQ-2025-0011",
    patient: "Diaz, Liana",
    patientDob: "2002-04-09",
    typePath: ["Patient", "Records"],
    requested: "Personal Records Release",
    quickFilterKey: "notCompleted",
    lifecycle: {
      category: "Closed",
      path: ["Closed", "Not Completed", "Unable to Process"],
      occurredAt: "2025-09-17T14:55:00Z",
    },
    initiatedBy: { id: "usr-lporter", name: "Leah Porter", role: "Medical Records" },
    initiatedAt: "2025-09-16T10:05:00Z",
    assignee: { id: "usr-lporter", name: "Leah Porter", role: "Medical Records" },
    lastEvent: { title: "Closed unable to process", occurredAt: "2025-09-17T14:55:00Z" },
    flags: ["Identity Verification Failed"],
    description: "Patient could not verify identity within SLA; request closed without fulfillment.",
    properties: [
      { label: "Patient", value: "Diaz, Liana" },
      { label: "Verification Attempts", value: "3" },
      { label: "Resubmission Allowed", value: "After Oct 1, 2025" },
    ],
    linkedConversations: {
      count: 1,
      items: [{ id: "conv-642", title: "Patient secure message thread", type: "Secure Message" }],
    },
    linkedActions: {
      count: 3,
      items: [
        { id: "act-1002", title: "Log identity verification failure", type: "Task" },
        { id: "item-1002", title: "Patient › Diaz, Liana", type: "Patient" },
        { id: "item-1003", title: "Patient › Diaz, Liana › Records Request", type: "Records" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-16T12:20:00Z",
        participant: { id: "prov-leahporter", name: "Leah Porter", role: "Records Specialist" },
      },
      secondary: undefined,
    },
  },
  {
    id: "REQ-2025-0012",
    patient: "Diaz, Ramon",
    patientDob: "1965-06-17",
    typePath: ["Patient", "Order"],
    requested: "Medication Refill Authorization",
    quickFilterKey: "canceled",
    lifecycle: {
      category: "Closed",
      path: ["Closed", "Canceled", "By Initiator"],
      occurredAt: "2025-09-18T09:32:00Z",
    },
    initiatedBy: { id: "usr-rsawyer", name: "Riley Sawyer", role: "Pharmacy Coordinator" },
    initiatedAt: "2025-09-17T08:48:00Z",
    assignee: { id: "usr-rsawyer", name: "Riley Sawyer", role: "Pharmacy Coordinator" },
    lastEvent: { title: "Canceled by initiator", occurredAt: "2025-09-18T09:32:00Z" },
    description: "Refill request withdrawn after patient transferred care to external provider.",
    properties: [
      { label: "Patient", value: "Diaz, Ramon" },
      { label: "Medication", value: "Sertraline 50mg" },
      { label: "Reason", value: "Patient moved care" },
    ],
    linkedConversations: {
      count: 1,
      items: [{ id: "conv-651", title: "Patient notification", type: "Secure Message" }],
    },
    linkedActions: {
      count: 3,
      items: [
        { id: "act-1007", title: "Document care transition", type: "Task" },
        { id: "item-1007", title: "Patient › Diaz, Ramon", type: "Patient" },
        { id: "item-1008", title: "Patient › Diaz, Ramon › Medication › Sertraline 50mg", type: "Order" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-17T10:10:00Z",
        participant: { id: "prov-rileysawyer", name: "Riley Sawyer", role: "Pharmacy Coordinator" },
      },
      secondary: undefined,
    },
  },
  {
    id: "REQ-2025-0013",
    patient: "Everett, Miles",
    patientDob: "1975-12-05",
    typePath: ["Patient", "Order"],
    requested: "Metabolic Panel Follow-up",
    quickFilterKey: "canceled",
    lifecycle: {
      category: "Closed",
      path: ["Closed", "Canceled", "By Operations"],
      occurredAt: "2025-09-16T07:18:00Z",
    },
    initiatedBy: { id: "usr-kklein", name: "Kara Klein", role: "Scheduling" },
    initiatedAt: "2025-09-15T13:12:00Z",
    assignee: { id: "usr-hcosta", name: "Hector Costa", role: "Clinical Reviewer" },
    lastEvent: { title: "Canceled by operations per duplicate order policy", occurredAt: "2025-09-16T07:18:00Z" },
    flags: ["Duplicate Order"],
    description: "Duplicate lab request detected by operations; order canceled and duplicate flagged.",
    properties: [
      { label: "Patient", value: "Everett, Miles" },
      { label: "Original Order", value: "LAB-7421" },
      { label: "Policy Reference", value: "Ops-CLN-204" },
    ],
    linkedConversations: {
      count: 2,
      items: [
        { id: "conv-661", title: "Operations review note", type: "Internal Note" },
        { id: "conv-662", title: "Clinician acknowledgement", type: "Team Message" },
      ],
    },
    linkedActions: {
      count: 3,
      items: [
        { id: "act-1011", title: "Audit duplicate lab", type: "Task" },
        { id: "item-1011", title: "Patient › Everett, Miles", type: "Patient" },
        { id: "item-1012", title: "Patient › Everett, Miles › Lab Order › LAB-7421", type: "Order" },
      ],
    },
    careTeam: {
      primary: {
        occurredAt: "2025-09-15T14:00:00Z",
        participant: { id: "prov-erinwake", name: "NP Erin Wake", role: "Ordering Provider" },
      },
      secondary: {
        occurredAt: "2025-09-15T14:20:00Z",
        participant: { id: "prov-georgelin", name: "NP George Lin", role: "Utilization NP" },
      },
    },
  },
];


export const HOME_ALERT_COUNT = REQUEST_CARD_FIXTURES.filter((card) =>
  card.quickFilterKey === "returned" || card.quickFilterKey === "hold",
).length;

const REQUEST_FILTER_PLACEHOLDER = "placeholder";
function useRequestCards() {
  return useMemo(() => REQUEST_CARD_FIXTURES, []);
}

const REQUEST_STATUS_META: Record<RequestQuickFilterKey, { className: string; icon: ReactNode }> = {
  draft: {
    className: "bg-slate-100 text-slate-800 border border-slate-200",
    icon: <Info className="size-3.5" />,
  },
  submitted: {
    className: "bg-sky-50 text-sky-900 border border-sky-200",
    icon: <ArrowRightCircle className="size-3.5" />,
  },
  approved: {
    className: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    icon: <CheckCircle2 className="size-3.5" />,
  },
  returned: {
    className: "bg-orange-50 text-orange-900 border border-orange-200",
    icon: <RotateCcw className="size-3.5" />,
  },
  hold: {
    className: "bg-purple-50 text-purple-900 border border-purple-200",
    icon: <PauseCircle className="size-3.5" />,
  },
  ineligible: {
    className: "bg-red-50 text-red-800 border border-red-200",
    icon: <AlertCircle className="size-3.5" />,
  },
  completed: {
    className: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    icon: <CheckCircle2 className="size-3.5" />,
  },
  notCompleted: {
    className: "bg-amber-50 text-amber-900 border border-amber-200",
    icon: <MinusCircle className="size-3.5" />,
  },
  canceled: {
    className: "bg-slate-100 text-slate-800 border border-slate-200",
    icon: <X className="size-3.5" />,
  },
};

type RequestQuickFilterGroupKey = "open" | "closed";

type RequestQuickFilterDefinition = {
  label: string;
  chips: Array<{
    key: RequestQuickFilterKey;
    label: string;
  }>;
};

const REQUEST_QUICK_FILTERS: Record<RequestQuickFilterGroupKey, RequestQuickFilterDefinition> = {
  open: {
    label: "Open",
    chips: [
      { key: "draft", label: "Draft" },
      { key: "submitted", label: "Submitted" },
      { key: "approved", label: "Approved" },
      { key: "returned", label: "Returned" },
      { key: "hold", label: "On Hold" },
    ],
  },
  closed: {
    label: "Closed",
    chips: [
      { key: "completed", label: "Completed" },
      { key: "notCompleted", label: "Not Completed" },
      { key: "ineligible", label: "Ineligible" },
      { key: "canceled", label: "Canceled" },
    ],
  },
};

type QuickFilterStyle = {
  icon: LucideIcon;
  activeClass: string;
  iconActiveClass: string;
};

const REQUEST_QUICK_FILTER_GROUP_META: Record<RequestQuickFilterGroupKey, QuickFilterStyle> = {
  open: {
    icon: FolderOpen,
    activeClass: "border-sky-200 bg-sky-50 text-sky-900",
    iconActiveClass: "text-sky-700",
  },
  closed: {
    icon: CheckCircle2,
    activeClass: "border-slate-300 bg-slate-50 text-slate-700",
    iconActiveClass: "text-slate-600",
  },
};

const REQUEST_QUICK_FILTER_CHIP_META: Record<RequestQuickFilterKey, QuickFilterStyle> = {
  draft: {
    icon: Info,
    activeClass: "border-slate-300 bg-slate-50 text-slate-700",
    iconActiveClass: "text-slate-600",
  },
  submitted: {
    icon: ArrowRightCircle,
    activeClass: "border-sky-300 bg-sky-50 text-sky-700",
    iconActiveClass: "text-sky-600",
  },
  hold: {
    icon: PauseCircle,
    activeClass: "border-purple-300 bg-purple-50 text-purple-700",
    iconActiveClass: "text-purple-600",
  },
  approved: {
    icon: CheckCircle2,
    activeClass: "border-emerald-300 bg-emerald-50 text-emerald-700",
    iconActiveClass: "text-emerald-600",
  },
  returned: {
    icon: RotateCcw,
    activeClass: "border-orange-300 bg-orange-50 text-orange-700",
    iconActiveClass: "text-orange-600",
  },
  ineligible: {
    icon: AlertCircle,
    activeClass: "border-red-300 bg-red-50 text-red-700",
    iconActiveClass: "text-red-600",
  },
  notCompleted: {
    icon: MinusCircle,
    activeClass: "border-amber-300 bg-amber-50 text-amber-700",
    iconActiveClass: "text-amber-600",
  },
  canceled: {
    icon: X,
    activeClass: "border-slate-300 bg-slate-50 text-slate-700",
    iconActiveClass: "text-slate-600",
  },
  completed: {
    icon: CheckCircle2,
    activeClass: "border-emerald-300 bg-emerald-50 text-emerald-700",
    iconActiveClass: "text-emerald-600",
  },
};

type RequestQuickFilterCounts = {
  groups: Record<RequestQuickFilterGroupKey, number>;
  chips: Record<RequestQuickFilterGroupKey, Partial<Record<RequestQuickFilterKey, number>>>;
};

// ------------- Calendar Mock
type VisitMessage = {
  id: string;
  sender: "Facility" | "Provider";
  body: string;
  timestamp: string;
};

type VisitEntry = {
  type: "Initial" | "Subsequent";
  patientShort: string;
  patientFull: string;
  provider: string;
  messages?: VisitMessage[];
};

type VisitModalState = {
  day: number;
  visits: VisitEntry[];
  isCompleted: boolean;
};

type VisitFilterFavoriteKey = "all" | "completed" | "initial" | "subsequent";

type VisitFilterFavorite = {
  value: VisitFilterFavoriteKey;
  label: string;
  description: string;
  visibility: {
    completed: boolean;
    initial: boolean;
    subsequent: boolean;
  };
};

const VISIT_FILTER_FAVORITES: VisitFilterFavorite[] = [
  {
    value: "all",
    label: "All Visit Activity",
    description: "Show completed and upcoming encounters.",
    visibility: { completed: true, initial: true, subsequent: true },
  },
  {
    value: "completed",
    label: "Completed Visits",
    description: "Only show days with completed encounters.",
    visibility: { completed: true, initial: false, subsequent: false },
  },
  {
    value: "initial",
    label: "Upcoming Initial Visits",
    description: "Show only upcoming initial encounters.",
    visibility: { completed: false, initial: true, subsequent: false },
  },
  {
    value: "subsequent",
    label: "Upcoming Subsequent Visits",
    description: "Show only upcoming subsequent encounters.",
    visibility: { completed: false, initial: false, subsequent: true },
  },
];

type VisitSchedule = Record<number, VisitEntry[]>;

const BASE_VISIT_SCHEDULE: VisitSchedule = {
  1: [
    {
      type: "Initial",
      patientShort: "Jared R.",
      patientFull: "Jared Rivera",
      provider: "Dr. Priya Patel",
      messages: [
        { id: "msg-1", sender: "Facility", body: "Please confirm lithium labs ahead of visit.", timestamp: "2025-09-28T11:30:00Z" },
        { id: "msg-2", sender: "Provider", body: "Confirmed—labs ordered for 9/30.", timestamp: "2025-09-28T14:05:00Z" },
      ],
    },
    { type: "Subsequent", patientShort: "Sara P.", patientFull: "Sara Park", provider: "NP George Lin" },
    { type: "Subsequent", patientShort: "Maya C.", patientFull: "Maya Coleman", provider: "NP George Lin" },
    { type: "Initial", patientShort: "Lena G.", patientFull: "Lena Grant", provider: "Dr. Priya Patel" },
    { type: "Subsequent", patientShort: "Kai J.", patientFull: "Kai Jordan", provider: "Dr. Elise Shaw" },
  ],
  3: [
    { type: "Subsequent", patientShort: "Alma W.", patientFull: "Alma Winters", provider: "PA Jordan Chu" },
    { type: "Initial", patientShort: "Ravi S.", patientFull: "Ravi Shah", provider: "Dr. Cameron Blake" },
    { type: "Subsequent", patientShort: "Bao N.", patientFull: "Bao Nguyen", provider: "PA Jordan Chu" },
    { type: "Subsequent", patientShort: "Cora M.", patientFull: "Cora Martinez", provider: "NP Leah Ortiz" },
    { type: "Initial", patientShort: "Tia B.", patientFull: "Tia Benson", provider: "Dr. Cameron Blake" },
  ],
  6: [
    { type: "Initial", patientShort: "Wyatt P.", patientFull: "Wyatt Pierce", provider: "Dr. Hannah Cho" },
    { type: "Subsequent", patientShort: "Zoey L.", patientFull: "Zoey Lawson", provider: "NP Erin Wake" },
  ],
  9: [
    { type: "Initial", patientShort: "Felix B.", patientFull: "Felix Bryant", provider: "Dr. Robyn Singh" },
    { type: "Subsequent", patientShort: "Dara O.", patientFull: "Dara Owens", provider: "NP Mira Lopez" },
    { type: "Subsequent", patientShort: "Hana K.", patientFull: "Hana Kim", provider: "NP Mira Lopez" },
    { type: "Initial", patientShort: "Evan R.", patientFull: "Evan Rhodes", provider: "Dr. Robyn Singh" },
    { type: "Subsequent", patientShort: "Gabe E.", patientFull: "Gabriel Estes", provider: "Dr. Sahana Iyer" },
    { type: "Subsequent", patientShort: "Lyla P.", patientFull: "Lyla Peters", provider: "NP Mira Lopez" },
  ],
  11: [
    {
      type: "Subsequent",
      patientShort: "Silas M.",
      patientFull: "Silas Monroe",
      provider: "NP Kendall Fox",
      messages: [
        { id: "msg-11a", sender: "Provider", body: "Confirmed meds delivered to facility pharmacy.", timestamp: "2025-09-27T09:45:00Z" },
      ],
    },
    { type: "Subsequent", patientShort: "Vera T.", patientFull: "Vera Thompson", provider: "NP Kendall Fox" },
    { type: "Initial", patientShort: "Iris P.", patientFull: "Iris Patel", provider: "Dr. Rey Ortiz" },
    { type: "Subsequent", patientShort: "Noah C.", patientFull: "Noah Carter", provider: "Dr. Rey Ortiz" },
  ],
  15: [
    { type: "Initial", patientShort: "April V.", patientFull: "April Vance", provider: "Dr. Hannah Cho" },
    { type: "Subsequent", patientShort: "Milo H.", patientFull: "Milo Hunter", provider: "NP Erin Wake" },
    { type: "Subsequent", patientShort: "Dana F.", patientFull: "Dana Flores", provider: "NP Erin Wake" },
  ],
  17: [
    { type: "Subsequent", patientShort: "Nico S.", patientFull: "Nico Salazar", provider: "PA Jordan Chu" },
    { type: "Initial", patientShort: "Cleo B.", patientFull: "Cleo Brooks", provider: "Dr. Cameron Blake" },
  ],
  23: [
    { type: "Initial", patientShort: "Ivy L.", patientFull: "Ivy Lam", provider: "Dr. Elise Shaw" },
    { type: "Subsequent", patientShort: "Pablo H.", patientFull: "Pablo Hernandez", provider: "NP George Lin" },
    { type: "Subsequent", patientShort: "Suri N.", patientFull: "Suri Nand", provider: "NP George Lin" },
    { type: "Initial", patientShort: "Corey F.", patientFull: "Corey Fitzgerald", provider: "Dr. Elise Shaw" },
  ],
  25: [
    { type: "Subsequent", patientShort: "Amir W.", patientFull: "Amir Wallace", provider: "NP Mira Lopez" },
    { type: "Subsequent", patientShort: "Nina K.", patientFull: "Nina Kang", provider: "NP Mira Lopez" },
    {
      type: "Initial",
      patientShort: "Owen R.",
      patientFull: "Owen Reeves",
      provider: "Dr. Rey Ortiz",
      messages: [
        { id: "msg-25a", sender: "Facility", body: "Family requested update post visit.", timestamp: "2025-09-26T10:12:00Z" },
      ],
    },
    { type: "Subsequent", patientShort: "Inez T.", patientFull: "Inez Torres", provider: "Dr. Rey Ortiz" },
    { type: "Initial", patientShort: "Lola S.", patientFull: "Lola Summers", provider: "Dr. Elise Shaw" },
  ],
  28: [
    { type: "Subsequent", patientShort: "Jada H.", patientFull: "Jada Herman", provider: "NP Kendall Fox" },
  ],
};

const BASE_COMPLETED_VISIT_DAYS = new Set<number>([1, 3, 9, 11]);
const BASE_NOT_ON_PROPERTY_DAYS = new Set<number>([5, 12, 18, 26]);

const getVisitSchedule = (year: number, month: number): VisitSchedule =>
  year === 2025 && month === 8 ? BASE_VISIT_SCHEDULE : {};

const getCompletedVisitDays = (year: number, month: number) =>
  year === 2025 && month === 8 ? new Set(BASE_COMPLETED_VISIT_DAYS) : new Set<number>();

const getNotOnPropertyDays = (year: number, month: number) =>
  year === 2025 && month === 8 ? new Set(BASE_NOT_ON_PROPERTY_DAYS) : new Set<number>();
const CalendarMock = () => {
  const canonicalMonth = useMemo(() => new Date(2025, 8, 1), []);
  const [currentMonth, setCurrentMonth] = useState(canonicalMonth);
  const [selectedFavorite, setSelectedFavorite] = useState<VisitFilterFavoriteKey>("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [modalState, setModalState] = useState<VisitModalState | null>(null);
  const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null);
  const [visitUiState, setVisitUiState] = useState<Record<string, { showComposer: boolean; showMessages: boolean; draft: string }>>({});

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = format(currentMonth, "MMMM yyyy");

  const visitSchedule = useMemo(() => getVisitSchedule(year, month), [year, month]);
  const completedVisitDays = useMemo(() => getCompletedVisitDays(year, month), [year, month]);
  const notOnPropertyDays = useMemo(() => getNotOnPropertyDays(year, month), [year, month]);

  const goMonth = (delta: number) => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };
  const resetFavorite = () => {
    setSelectedFavorite("all");
  };

  const activeFiltersCount = selectedFavorite === "all" ? 0 : 1;
  const favoriteConfig =
    VISIT_FILTER_FAVORITES.find((entry) => entry.value === selectedFavorite) ?? VISIT_FILTER_FAVORITES[0];

  const handleOpenModal = (day: number, visits: VisitEntry[], isCompletedDay: boolean) => {
    const sorted = visits.slice().sort((a, b) => {
      const aHasMessages = (a.messages?.length ?? 0) > 0;
      const bHasMessages = (b.messages?.length ?? 0) > 0;
      if (aHasMessages === bHasMessages) {
        return 0;
      }
      return aHasMessages ? -1 : 1;
    });
    const initial: Record<string, { showComposer: boolean; showMessages: boolean; draft: string }> = {};
    sorted.forEach((_, idx) => {
      const key = `${day}-${idx}`;
      initial[key] = {
        showComposer: false,
        showMessages: false,
        draft: "",
      };
    });
    setVisitUiState(initial);
    setActiveMenuKey(null);
    setModalState({ day, visits: sorted, isCompleted: isCompletedDay });
  };

  const handleCloseModal = () => {
    setVisitUiState({});
    setActiveMenuKey(null);
    setModalState(null);
  };

  const updateVisitUi = (key: string, updater: (prev: { showComposer: boolean; showMessages: boolean; draft: string }) => { showComposer: boolean; showMessages: boolean; draft: string }) => {
    setVisitUiState((prev) => ({
      ...prev,
      [key]: updater(prev[key] ?? { showComposer: false, showMessages: false, draft: "" }),
    }));
  };

  const handleToggleMessages = (visitKey: string) => {
    setActiveMenuKey(null);
    updateVisitUi(visitKey, (prev) => ({ ...prev, showMessages: !prev.showMessages, showComposer: !prev.showMessages }));
  };

  const handleDraftChange = (visitKey: string, value: string) => {
    updateVisitUi(visitKey, (prev) => ({ ...prev, draft: value }));
  };

const handleCancelMessage = (visitKey: string) => {
    updateVisitUi(visitKey, (prev) => ({ ...prev, showComposer: false, showMessages: false, draft: "" }));
  };

  const handleSendMessage = (visitKey: string) => {
    if (!modalState) {
      return;
    }
    const visitIndex = modalState.visits.findIndex((_, idx) => `${modalState.day}-${idx}` === visitKey);
    if (visitIndex === -1) {
      return;
    }
    const draft = visitUiState[visitKey]?.draft?.trim();
    if (!draft) {
      return;
    }
    const newVisits = modalState.visits.map((visit, idx) => {
      if (idx !== visitIndex) {
        return visit;
      }
      return {
        ...visit,
        messages: [
          ...(visit.messages ?? []),
          {
            id: `msg-${visitKey}-${Date.now()}`,
            sender: "Facility" as const,
            body: draft,
            timestamp: new Date().toISOString(),
          },
        ],
      };
    });
    setModalState({ ...modalState, visits: newVisits });
    updateVisitUi(visitKey, () => ({ showComposer: false, showMessages: true, draft: "" }));
  };

  const firstDay = new Date(year, month, 1);
  const firstWeekday = (firstDay.getDay() + 6) % 7; // convert to Monday-start

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];

  for (let i = 0; i < firstWeekday; i += 1) {
    currentWeek.push(null);
  }

  for (let currentDay = 1; currentDay <= daysInMonth; currentDay += 1) {
    currentWeek.push(currentDay);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  const shouldShowVisit = (visitType: "Initial" | "Subsequent", isCompletedDay: boolean) => {
    if (isCompletedDay) {
      return favoriteConfig.visibility.completed;
    }
    if (visitType === "Initial") {
      return favoriteConfig.visibility.initial;
    }
    return favoriteConfig.visibility.subsequent;
  };

  let completedTotal = 0;
  let upcomingInitialTotal = 0;
  let upcomingSubsequentTotal = 0;
  Object.entries(visitSchedule).forEach(([dayValue, visits]) => {
    const dayNumber = Number(dayValue);
    const isCompletedDayValue = completedVisitDays.has(dayNumber);
    if (isCompletedDayValue) {
      completedTotal += visits.length;
    } else {
      visits.forEach((visit) => {
        if (visit.type === "Initial") {
          upcomingInitialTotal += 1;
        } else {
          upcomingSubsequentTotal += 1;
        }
      });
    }
  });
  const legendItems = [
    {
      key: 'completed',
      label: 'Completed',
      icon: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />,
      count: completedTotal,
    },
    {
      key: 'initial',
      label: 'Initial',
      icon: <UserPlus className="h-3.5 w-3.5 text-sky-600" />,
      count: upcomingInitialTotal,
    },
    {
      key: 'subsequent',
      label: 'Subsequent',
      icon: <RefreshCcw className="h-3.5 w-3.5 text-blue-600" />,
      count: upcomingSubsequentTotal,
    },
    {
      key: 'notOnProperty',
      label: 'Not on Property',
      icon: <span className="inline-flex h-3.5 w-3.5 rounded-sm bg-slate-400" />,
      count: notOnPropertyDays.size,
    },
  ];

  const modalDate = modalState ? new Date(year, month, modalState.day) : null;
  const modalDateLabel = modalDate ? dateRangeFormatter.format(modalDate) : '';

  return (
    <>
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="right" className="w-full sm:w-[360px]">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-3 text-sm text-muted-foreground">
            <p>Filter controls coming soon.</p>
          </div>
        </SheetContent>
      </Sheet>
      <Card>
        <CardHeader className="space-y-4 pb-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-col gap-1">
              <CardTitle className="text-base font-semibold text-foreground sm:text-lg">
                Calendar View for Encounters (Visits)
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => goMonth(-1)} aria-label="Previous month">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-lg font-semibold sm:text-xl">{monthLabel}</div>
                <Button variant="outline" size="sm" onClick={() => goMonth(1)} aria-label="Next month">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(canonicalMonth)}>
                  Today
                </Button>
              </div>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:items-end">
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                <Button variant="outline" className="gap-2" onClick={() => setFiltersOpen(true)}>
                  <Filter className="h-4 w-4" />
                  Filters
                  {activeFiltersCount > 0 && <Badge className="ml-1 h-5 px-1 py-0">{activeFiltersCount}</Badge>}
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={resetFavorite}
                      disabled={activeFiltersCount === 0}
                      aria-label="Reset filters"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Reset filters</TooltipContent>
                </Tooltip>
                <Select
                  value={selectedFavorite}
                  onValueChange={(value) => setSelectedFavorite(value as VisitFilterFavoriteKey)}
                >
                  <SelectTrigger className="w-full min-w-[12rem] sm:w-[14rem]" aria-label="Visit filter favorites">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VISIT_FILTER_FAVORITES.map((favorite) => (
                      <SelectItem key={favorite.value} value={favorite.value}>
                        {favorite.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <span className="text-xs text-muted-foreground sm:text-right">
                {favoriteConfig.description}
              </span>
              <div className="flex w-full flex-wrap items-center gap-2 overflow-x-auto sm:w-auto sm:justify-end sm:gap-3">
                {legendItems.map((item) => (
                  <span
                    key={item.key}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-2 py-1 text-[11px] font-medium text-muted-foreground"
                  >
                    <span className="inline-flex items-center justify-center">{item.icon}</span>
                    <span className="hidden sm:inline">{item.label}</span>
                    <span className="text-xs font-semibold text-foreground">{item.count}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-7 text-[11px] uppercase tracking-wide text-muted-foreground">
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => (
              <div key={d} className="px-1 py-1 text-center font-medium">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weeks.flat().map((currentDay, index) => {
              if (currentDay === null) {
                return <div key={`${index}-empty`} className="aspect-square rounded-sm bg-transparent" />;
              }
              const visits = visitSchedule[currentDay] ?? [];
              const hasVisits = visits.length > 0;
              const isCompletedDay = completedVisitDays.has(currentDay);
              const isNotOnProperty = notOnPropertyDays.has(currentDay);
              const visibleVisits = hasVisits
                ? visits.filter((visit) => shouldShowVisit(visit.type, isCompletedDay))
                : [];
              const counts = {
                completed: isCompletedDay ? visibleVisits.length : 0,
                initial: !isCompletedDay ? visibleVisits.filter((visit) => visit.type === "Initial").length : 0,
                subsequent: !isCompletedDay ? visibleVisits.filter((visit) => visit.type === "Subsequent").length : 0,
              };
              const totalCount = counts.completed + counts.initial + counts.subsequent;
              const hasUnread = visibleVisits.some((visit) => (visit.messages?.length ?? 0) > 0);
              const isVisuallyEmpty = visibleVisits.length === 0;
              const canOpen = !isVisuallyEmpty && !isNotOnProperty;

                  const cellClass = cn(
                    "relative aspect-square rounded-sm border border-transparent bg-muted/40 p-2 text-left text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    hasVisits && !isCompletedDay && !isNotOnProperty && "bg-background border-border hover:shadow-sm",
                    isCompletedDay && !isNotOnProperty && "bg-emerald-50 border-emerald-200 hover:shadow-sm",
                    isNotOnProperty && "cursor-default bg-slate-100 border-slate-300 text-slate-600",
                    (isVisuallyEmpty || !hasVisits) && !isNotOnProperty && "cursor-default bg-muted/20 text-muted-foreground",
                  );
                  const dayNumberClasses = cn(
                    "absolute left-1 top-1 text-xs font-semibold sm:left-2 sm:top-2 sm:text-sm",
                    isNotOnProperty
                      ? "text-slate-600"
                      : isCompletedDay
                        ? "text-emerald-800"
                        : !isVisuallyEmpty
                          ? "text-foreground"
                          : "text-muted-foreground",
                  );
                  return (
                    <button
                      key={`${index}-${currentDay}`}
                      type="button"
                      className={cellClass}
                  onClick={() => {
                    if (!canOpen) return;
                    handleOpenModal(currentDay, visibleVisits, isCompletedDay);
                  }}
                  disabled={!canOpen}
                    >
                      <span className={dayNumberClasses}>{currentDay}</span>
                      {hasUnread ? (
                        <span className="absolute right-1 top-1 inline-flex size-2.5 rounded-full bg-sky-500 sm:right-2 sm:top-2" />
                      ) : null}
                  {isNotOnProperty ? (
                    <div className="absolute inset-x-2 bottom-2 top-7 flex items-center justify-center">
                      <span className="rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                        Not on property
                      </span>
                    </div>
                  ) : (
                    <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1 text-[10px]">
                      {counts.completed > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-100 px-1.5 py-[1px] font-medium text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="hidden sm:inline">{counts.completed}</span>
                        </span>
                      ) : null}
                      {counts.initial > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-sky-100 px-1.5 py-[1px] font-medium text-sky-700">
                          <UserPlus className="h-3 w-3" />
                          <span className="hidden sm:inline">{counts.initial}</span>
                        </span>
                      ) : null}
                      {counts.subsequent > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-blue-100 px-1.5 py-[1px] font-medium text-blue-700">
                          <RefreshCcw className="h-3 w-3" />
                          <span className="hidden sm:inline">{counts.subsequent}</span>
                        </span>
                      ) : null}
                      {totalCount > 0 ? (
                        <span className="ml-auto hidden text-[10px] text-muted-foreground sm:inline">{totalCount}</span>
                      ) : null}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      {modalState ? (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={handleCloseModal} aria-hidden />
          <div className="relative z-10 flex min-h-full items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-xl border bg-card p-5 shadow-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="text-lg font-semibold">Visits for {modalDateLabel}</div>
                  <div className="text-xs text-muted-foreground">
                    {modalState.visits.length} visit{modalState.visits.length === 1 ? '' : 's'}
                  </div>
                  {modalState.isCompleted ? (
                    <Badge className="mt-1 flex w-fit items-center gap-1 bg-emerald-50 text-emerald-900">
                      <CheckCircle2 className="size-3" />
                      Completed Day
                    </Badge>
                  ) : null}
                </div>
                <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                  <X className="size-4" />
                </Button>
              </div>
              <div className="mt-4 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
                {modalState.visits.length === 0 ? (
                  <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                    No visits match the current filter selection for this day.
                  </div>
                ) : (
                  modalState.visits.map((visit, idx) => {
                    const visitKey = `${modalState.day}-${idx}`;
                    const ui = visitUiState[visitKey] ?? { showComposer: false, showMessages: false, draft: "" };
                    const messages = visit.messages ?? [];
                    const hasMessages = messages.length > 0;
                    const isMenuOpen = activeMenuKey === visitKey;
                    return (
                      <div
                        key={visitKey}
                        className="rounded-lg border bg-muted/30 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                              <span className="font-mono">{visit.type === 'Initial' ? 'INI' : 'SUB'}</span>
                              <span>•</span>
                              <span>{visit.provider}</span>
                            </div>
                            <div className="text-sm font-semibold">{visit.patientFull}</div>
                            <div className="text-xs text-muted-foreground">Visit type: {visit.type}</div>
                            {hasMessages ? (
                              <button
                                type="button"
                                onClick={() => handleToggleMessages(visitKey)}
                                className="flex w-fit items-center gap-2 rounded-md border border-transparent bg-sky-100 px-2.5 py-1 text-[11px] font-medium text-sky-900 transition hover:bg-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200"
                              >
                                <MessageSquare className="size-3.5" />
                                <span className="font-semibold">{messages.length}</span>
                                <span>Messages</span>
                              </button>
                            ) : null}
                          </div>
                          <Popover open={isMenuOpen} onOpenChange={(open) => setActiveMenuKey(open ? visitKey : null)}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="icon" className="shrink-0">
                                <span className="sr-only">Visit actions</span>
                                <svg
                                  className="size-4"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden
                                >
                                  <circle cx="10" cy="4" r="1.5" />
                                  <circle cx="10" cy="10" r="1.5" />
                                  <circle cx="10" cy="16" r="1.5" />
                                </svg>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-44 p-1 text-sm">
                              <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted"
                                onClick={() => handleToggleMessages(visitKey)}
                              >
                                <span className="inline-flex size-3.5 items-center justify-center font-mono text-[11px]">{hasMessages ? messages.length : "0"}</span>
                                View Messages
                              </button>
                            </PopoverContent>
                          </Popover>
                        </div>
                        {ui.showMessages ? (
                          <div className="mt-3 space-y-3">
                            {messages.length > 0 ? (
                              <div className="space-y-2 rounded-md border border-slate-200 bg-background p-2 text-xs">
                                {messages.map((message) => (
                                  <div key={message.id} className="space-y-0.5">
                                    <div className="flex items-center justify-between font-medium">
                                      <span>{message.sender === 'Facility' ? 'You' : visit.provider}</span>
                                      <span className="text-[10px] text-muted-foreground">
                                        {formatDateTimeWithRelative(message.timestamp)}
                                      </span>
                                    </div>
                                    <p className="text-muted-foreground">{message.body}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="rounded-md border border-slate-200 bg-background p-3 text-xs text-muted-foreground">
                                No messages yet. Start a thread with the provider.
                              </div>
                            )}
                            <div className="space-y-2">
                              <Textarea
                                value={ui.draft}
                                onChange={(event) => handleDraftChange(visitKey, event.target.value)}
                                placeholder="Add a note for the provider..."
                                rows={3}
                              />
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleSendMessage(visitKey)}
                                  disabled={!ui.draft.trim()}
                                >
                                  Send Message
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleCancelMessage(visitKey)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={handleCloseModal}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

// ------------- Layout
type DashboardWindowSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  label?: string;
  className?: string;
};

function DashboardWindowSelect({
  value,
  onValueChange,
  label = "Time Period",
  className,
}: DashboardWindowSelectProps) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <span className="text-xs font-medium uppercase text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {WINDOW_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DashboardScaffold({
  children,
  searchPlaceholder,
  defaultWindow = "this_week",
  leftControl,
  rightControl,
  header,
}: PropsWithChildren<{
  searchPlaceholder: string;
  defaultWindow?: string;
  leftControl?: ReactNode;
  rightControl?: ReactNode;
  header?: ReactNode;
}>) {
  const [windowValue, setWindowValue] = useState<string>(defaultWindow);
  const contextValue = useMemo<DashboardWindowContextValue>(
    () => ({
      value: windowValue,
      label: WINDOW_LABEL_MAP[windowValue] ?? WINDOW_LABEL_MAP.this_week,
      setValue: setWindowValue,
    }),
    [windowValue],
  );

  const renderedChildren = useMemo(() => {
    return typeof children === "function"
      ? (children as (ctx: DashboardWindowContextValue) => ReactNode)(contextValue)
      : children;
  }, [children, contextValue]);

  const defaultLeftControl = (
    <DashboardWindowSelect value={windowValue} onValueChange={setWindowValue} />
  );

  const defaultRightControl = (
    <div className="relative w-full sm:max-w-xs lg:max-w-sm">
      <Input className="pl-8" placeholder={searchPlaceholder} />
      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
    </div>
  );

  const headerControl = leftControl ?? defaultLeftControl;
  const rightSideControl = rightControl === undefined ? defaultRightControl : rightControl;

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const headerNode =
    header ??
    (
      <div className="rounded-xl border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-4 md:flex-nowrap md:gap-x-5">
          {headerControl}
          {rightSideControl ? (
            <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
              {rightSideControl}
            </div>
          ) : null}
        </div>
      </div>
    );

  return (
    <DashboardWindowContext.Provider value={contextValue}>
      <div ref={scrollContainerRef} data-dashboard-scroll className="flex min-h-screen flex-col gap-6 overflow-y-auto">
        {headerNode}
        <div className="space-y-4 pb-10">{renderedChildren}</div>
      </div>
    </DashboardWindowContext.Provider>
  );
}

export function CensusView() {
  const { roleKey, roleConfig } = useRole();
  const isFacility = roleKey === "facilityNonMedical" || roleKey === "facilityMedical";

  const [mode, setMode] = useState<CensusViewMode>('schedule');
  const [selectedMetric, setSelectedMetric] = useState<string>(CENSUS_METRICS[0]?.label ?? 'Active Census');
  const [selectedTrendMetric, setSelectedTrendMetric] = useState<CensusTrendMetricKey>('active');
  const [trendDateRange, setTrendDateRange] = useState<ActionDateRangeValue>(() => getCurrentMonthRange(new Date()));
  const [trendDateRangePreset, setTrendDateRangePreset] = useState<string>('Current Month');
  const [trendDateRangeModalOpen, setTrendDateRangeModalOpen] = useState(false);
  const trendDateRangeSummary = useMemo(() => {
    const base = formatActionDateRange(trendDateRange);
    if (!trendDateRangePreset || base === 'Select range') {
      return base;
    }
    return `${base} • ${trendDateRangePreset}`;
  }, [trendDateRange, trendDateRangePreset]);

  const handleTrendDateRangePresetSelect = useCallback((label: string) => {
    const preset = ACTION_DATE_RANGE_PRESETS.find((candidate) => candidate.label === label);
    if (!preset) {
      return;
    }
    const range = preset.getRange(new Date());
    setTrendDateRange({ start: range.start, end: range.end });
    setTrendDateRangePreset(label);
  }, []);

  const handleTrendDateRangeStartChange = useCallback((date: Date | undefined) => {
    if (!date) {
      return;
    }
    setTrendDateRange((prev) => {
      const nextStart = startOfDay(date);
      if (nextStart > prev.end) {
        return { start: nextStart, end: endOfDay(nextStart) };
      }
      return { start: nextStart, end: prev.end };
    });
    setTrendDateRangePreset('Custom');
  }, []);

  const handleTrendDateRangeEndChange = useCallback((date: Date | undefined) => {
    if (!date) {
      return;
    }
    setTrendDateRange((prev) => {
      const nextEnd = endOfDay(date);
      if (nextEnd < prev.start) {
        return { start: startOfDay(nextEnd), end: nextEnd };
      }
      return { start: prev.start, end: nextEnd };
    });
    setTrendDateRangePreset('Custom');
  }, []);

  const handleTrendDateRangeModalClose = useCallback(() => {
    setTrendDateRangeModalOpen(false);
  }, []);

  const handleTrendDateRangeModalApply = useCallback(() => {
    setTrendDateRangeModalOpen(false);
  }, []);

  const handleHeaderMetricSelect = useCallback(
    (label: string) => {
      setSelectedMetric(label);
      setMode('tabular');
    },
    [setMode, setSelectedMetric]
  );

  const handleTableMetricSelect = useCallback((label: string) => {
    setSelectedMetric(label);
  }, []);

  const isTrendDateRangeModalVisible = mode === 'trends' && trendDateRangeModalOpen;

  return (
    <>
      <DashboardScaffold
        searchPlaceholder="Search census"
        defaultWindow="this_month"
        header={
          isFacility ? (
            <CensusHeader
              mode={mode}
              onModeChange={setMode}
              selectedMetric={selectedMetric}
              onSelectMetric={handleHeaderMetricSelect}
              trendMetric={selectedTrendMetric}
              onTrendMetricChange={setSelectedTrendMetric}
              onOpenTrendRangeModal={() => setTrendDateRangeModalOpen(true)}
            />
          ) : undefined
        }
      >
        {isFacility ? (
          <CensusContent
            mode={mode}
            selectedMetric={selectedMetric}
            onSelectMetric={handleTableMetricSelect}
            selectedTrendMetric={selectedTrendMetric}
            trendDateRange={trendDateRange}
          />
        ) : (
          <RolePlaceholder title={`${roleConfig.label} · Census`} />
        )}
      </DashboardScaffold>
      {isTrendDateRangeModalVisible ? (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={handleTrendDateRangeModalClose} aria-hidden />
          <div className="relative z-10 flex min-h-full items-center justify-center p-4">
            <div className="w-full max-w-5xl rounded-xl border bg-card p-6 shadow-2xl">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold leading-tight text-foreground">Adjust Date Range</h2>
                  <p className="text-sm text-muted-foreground">
                    Choose a preset or set custom dates. Current selection: {trendDateRangeSummary}.
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={handleTrendDateRangeModalClose}>
                  <X className="size-4" />
                </Button>
              </div>
              <div className="mt-6 grid gap-6 lg:grid-cols-[0.5fr_1.5fr]">
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground">Quick ranges</h3>
                  <div className="grid gap-2">
                    {ACTION_DATE_RANGE_PRESETS.map((preset) => (
                      <Button
                        key={preset.label}
                        variant={preset.label === trendDateRangePreset ? 'secondary' : 'ghost'}
                        size="sm"
                        className="justify-start"
                        onClick={() => handleTrendDateRangePresetSelect(preset.label)}
                      >
                        <span className="flex flex-col text-left">
                          <span>{preset.label}</span>
                          {preset.description ? (
                            <span className="text-[11px] font-normal text-muted-foreground">
                              {preset.description}
                            </span>
                          ) : null}
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground">Custom range</h3>
                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground/80">Start</div>
                      <Calendar
                        selected={trendDateRange.start}
                        onSelect={handleTrendDateRangeStartChange}
                        className="rounded-md border !p-2"
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground/80">End</div>
                      <Calendar
                        selected={trendDateRange.end}
                        onSelect={handleTrendDateRangeEndChange}
                        className="rounded-md border !p-2"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={handleTrendDateRangeModalClose}>
                  Cancel
                </Button>
                <Button onClick={handleTrendDateRangeModalApply}>Apply</Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

type RequestKpiMetric = {
  key: string;
  label: string;
  value: number;
  icon: LucideIcon;
};

type RequestViewMode = "cards" | "table";

const REQUEST_KPI_METRICS: RequestKpiMetric[] = [
  { key: "open", label: "Open (All Statuses)", value: 24, icon: FolderOpen },
  { key: "initiatedByMe", label: "Initiated by Me (Open)", value: 8, icon: UserPlus },
  { key: "assignedToMe", label: "Assigned to Me (Open)", value: 12, icon: UserCheck },
  { key: "returned30d", label: "Returned (Open)", value: 6, icon: RotateCcw },
  { key: "closed30d", label: "Closed (Last 30d)", value: 44, icon: CheckCircle2 },
];

function RequestKpiCard({ metric }: { metric: RequestKpiMetric }) {
  const Icon = metric.icon;
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-lg bg-muted/30 px-3 py-2 text-left shadow-sm",
        "md:w-[7.25rem] lg:w-[8rem]",
      )}
      aria-label={`${metric.label}: ${metric.value}`}
    >
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground leading-4 min-h-[2.25rem]">
        {metric.label}
      </span>
      <span className="flex items-center gap-2">
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-background/60">
          <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        </span>
        <span className="text-2xl font-semibold text-foreground">
          {metric.value.toLocaleString()}
        </span>
      </span>
    </div>
  );
}

function RequestsDashboardHeader({
  viewMode,
  onViewModeChange,
  onOpenFilters,
  onResetFilters,
  filterFavorite,
  onFilterFavoriteChange,
  activeFiltersCount,
  onOpenNewRequest,
  searchQuery,
  onSearchChange,
  quickStatusGroup,
  onQuickStatusGroupChange,
  quickStatusKey,
  onQuickStatusKeyChange,
  quickFilterCounts,
}: {
  viewMode: RequestViewMode;
  onViewModeChange: (mode: RequestViewMode) => void;
  onOpenFilters: () => void;
  onResetFilters: () => void;
  filterFavorite: string;
  onFilterFavoriteChange: (value: string) => void;
  activeFiltersCount: number;
  onOpenNewRequest: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  quickStatusGroup: RequestQuickFilterGroupKey;
  onQuickStatusGroupChange: (group: RequestQuickFilterGroupKey) => void;
  quickStatusKey: RequestQuickFilterKey | null;
  onQuickStatusKeyChange: (key: RequestQuickFilterKey | null) => void;
  quickFilterCounts: RequestQuickFilterCounts;
}) {
  const isResetDisabled = activeFiltersCount === 0;
  const groupDefinition = REQUEST_QUICK_FILTERS[quickStatusGroup];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex md:flex-row md:items-start md:justify-between md:gap-8">
          <div className="space-y-1 md:flex-none md:min-w-[16rem] md:max-w-[22rem]">
            <CardTitle className="text-2xl font-semibold text-foreground">Requests</CardTitle>
            <CardDescription>Current and past request information</CardDescription>
          </div>
          <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-3 md:flex md:flex-1 md:flex-wrap md:justify-end md:gap-4">
            {REQUEST_KPI_METRICS.map((metric) => (
              <RequestKpiCard key={metric.key} metric={metric} />
            ))}
          </div>
        </CardHeader>
      </Card>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Tabs
            value={viewMode}
            onValueChange={(next) => {
              const nextMode: RequestViewMode = next === "table" ? "table" : "cards";
              onViewModeChange(nextMode);
            }}
            className="w-full lg:w-auto"
          >
            <TabsList className="grid w-full grid-cols-2 sm:w-48">
              <TabsTrigger value="cards">Cards</TabsTrigger>
              <TabsTrigger value="table">Table</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <div className="relative w-full sm:w-64 lg:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search requests"
                className="w-full pl-9"
                aria-label="Search requests"
              />
            </div>
            <Button onClick={onOpenNewRequest} className="gap-2 self-end sm:self-auto">
              <PlusCircle className="h-4 w-4" />
              New Request
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex w-full flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 p-1">
              {(["open", "closed"] as RequestQuickFilterGroupKey[]).map((groupKey) => {
                const isActive = groupKey === quickStatusGroup;
                const count = quickFilterCounts.groups[groupKey] ?? 0;
                const groupMeta = REQUEST_QUICK_FILTER_GROUP_META[groupKey];
                const GroupIcon = groupMeta.icon;
                return (
                  <Button
                    key={groupKey}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
                      isActive
                        ? cn(groupMeta.activeClass, "shadow-sm")
                        : "border-transparent text-muted-foreground hover:bg-muted/60",
                    )}
                    onClick={() => onQuickStatusGroupChange(groupKey)}
                  >
                    <span
                      className={cn(
                        "inline-flex h-3.5 w-3.5 items-center justify-center text-muted-foreground",
                        "md:hidden",
                        "[@media(min-width:1385px)]:inline-flex",
                        isActive ? groupMeta.iconActiveClass : undefined,
                      )}
                    >
                      <GroupIcon className="h-3.5 w-3.5" />
                    </span>
                    <span className="sr-only">{REQUEST_QUICK_FILTERS[groupKey].label}</span>
                    <span className="hidden md:inline md:truncate">{REQUEST_QUICK_FILTERS[groupKey].label}</span>
                    <span className="ml-1 text-xs font-semibold text-foreground md:ml-2 lg:ml-1">{count}</span>
                  </Button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {groupDefinition.chips.map((chip) => {
                const isActive = chip.key === quickStatusKey;
                const chipCount = quickFilterCounts.chips[quickStatusGroup]?.[chip.key] ?? 0;
                const chipMeta = REQUEST_QUICK_FILTER_CHIP_META[chip.key];
                const ChipIcon = chipMeta.icon;
                return (
                  <Button
                    key={chip.key}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium",
                      isActive
                        ? cn(chipMeta.activeClass, "shadow-sm")
                        : "border-border bg-card text-muted-foreground hover:bg-muted/60",
                    )}
                    onClick={() => onQuickStatusKeyChange(isActive ? null : chip.key)}
                  >
                    <span
                      className={cn(
                        "inline-flex h-3.5 w-3.5 items-center justify-center text-muted-foreground",
                        "md:hidden",
                        "[@media(min-width:1385px)]:inline-flex",
                        isActive ? chipMeta.iconActiveClass : undefined,
                      )}
                    >
                      <ChipIcon className="h-3.5 w-3.5" />
                    </span>
                    <span className="sr-only">{chip.label}</span>
                    <span className="hidden md:inline md:truncate">{chip.label}</span>
                    <span className="ml-1 text-[11px] font-semibold text-foreground md:ml-2 lg:ml-1">{chipCount}</span>
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center justify-end gap-2 lg:w-auto lg:flex-nowrap">
            <Button variant="outline" className="gap-2" onClick={onOpenFilters}>
              <Filter className="h-4 w-4" />
              Filters
              {activeFiltersCount > 0 ? (
                <Badge className="ml-1 h-5 px-1 py-0">{activeFiltersCount}</Badge>
              ) : null}
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onResetFilters}
                  disabled={isResetDisabled}
                  aria-label="Reset filters"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset filters</TooltipContent>
            </Tooltip>
            <Select value={filterFavorite} onValueChange={onFilterFavoriteChange}>
              <SelectTrigger className="w-full min-w-[10rem] sm:w-[12rem] lg:w-[14rem]" aria-label="Request filter favorites">
                <SelectValue placeholder="Filter favorites..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={REQUEST_FILTER_PLACEHOLDER}>Filter favorites...</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

    </div>
  );
}

function RequestsEmptyState({ message }: { message: string }) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        {message}
      </CardContent>
    </Card>
  );
}

const renderLifecyclePath = (
  path: string[],
  {
    mutedClass = "font-normal",
    activeClass = "font-semibold",
    separatorClass = "text-muted-foreground",
  }: {
    mutedClass?: string;
    activeClass?: string;
    separatorClass?: string;
  } = {},
) => {
  return path.map((segment, index) => {
    const isLast = index === path.length - 1;
    return (
      <Fragment key={`${segment}-${index}`}>
        {index > 0 ? (
          <span className={cn("px-1", separatorClass)} aria-hidden="true">
            »
          </span>
        ) : null}
        <span className={cn(isLast ? activeClass : mutedClass)}>{segment}</span>
      </Fragment>
    );
  });
};

function RequestCardItem({ card }: { card: RequestCard }) {
  const [activeTab, setActiveTab] = useState<RequestCardTabKey>("overview");

  const linkedCount = (card.linkedConversations?.count ?? 0) + (card.linkedActions?.count ?? 0);
  const tabs: Array<{ key: RequestCardTabKey; label: string; icon: LucideIcon; count?: number }> = [
    { key: "overview", label: "Overview", icon: ClipboardList },
    { key: "timeline", label: "Additional Information", icon: FileText },
    { key: "linked", label: "Linked", icon: LinkIcon, count: linkedCount },
  ];

  const lifecyclePath = card.lifecycle.path;
  const initiatedTimestamp = formatDateTime(card.initiatedAt);
  const patientDobLabel = card.patientDob
    ? dateRangeFormatter.format(new Date(card.patientDob))
    : null;
  const statusMeta = REQUEST_STATUS_META[card.quickFilterKey] ?? {
    className: "bg-muted text-foreground border border-border",
    icon: <Info className="size-3.5" />,
  };

  const formatAssignmentLine = (
    occurredAt?: string,
    participant?: RequestParticipant,
    fallback?: string,
  ): Array<{ value: string; tone: "muted" | "strong" }> => {
    if (!occurredAt || !participant) {
      return fallback ? [{ value: fallback, tone: "muted" }] : [];
    }
    const segments: Array<{ value: string; tone: "muted" | "strong" }> = [
      { value: formatDateTime(occurredAt), tone: "muted" },
      { value: participant.name, tone: "strong" },
    ];
    if (participant.role) {
      segments.push({ value: participant.role, tone: "muted" });
    }
    return segments;
  };

  const formatEventLine = (occurredAt?: string, title?: string) => {
    const segments: Array<{ value: string; tone: "muted" | "strong" }> = [
      { value: formatDateTime(occurredAt), tone: "muted" },
    ];
    if (title) {
      segments.push({ value: title, tone: "strong" });
    }
    return segments;
  };

  const renderInfoRow = (
    label: string,
    segments: Array<{ value: string; tone: "muted" | "strong" }>,
    fallback: string = "—",
  ) => (
    <div key={label} className="space-y-1">
      <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {segments.length === 0
          ? (
            <span className="text-xs text-muted-foreground">{fallback}</span>
          ) : (
            segments.map((segment, index) => (
              <Fragment key={`${label}-${segment.value}-${index}`}>
                <span className={segment.tone === "strong" ? "font-medium text-foreground" : "text-xs text-muted-foreground"}>
                  {segment.value}
                </span>
                {index < segments.length - 1 ? <span className="text-muted-foreground">·</span> : null}
              </Fragment>
            ))
          )}
      </div>
    </div>
  );

  const renderLinkedChip = (item: RequestLinkedItem) => (
    <span
      key={item.id}
      className="inline-flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-1 text-xs"
    >
      <LinkIcon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-[11px] uppercase text-muted-foreground">{item.type}</span>
      <span className="text-muted-foreground">›</span>
      <span className="font-medium text-foreground">{item.title}</span>
    </span>
  );

  const renderLinkedGroup = (
    label: string,
    items: RequestLinkedItem[],
    emptyMessage: string,
  ) => (
    <div key={label} className="space-y-2">
      <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => renderLinkedChip(item))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">{emptyMessage}</div>
      )}
    </div>
  );

  const currentAssigneeLine = formatAssignmentLine(card.lifecycle.occurredAt, card.assignee);
  const primaryProviderLine = formatAssignmentLine(
    card.careTeam?.primary?.occurredAt,
    card.careTeam?.primary?.participant,
  );
  const secondaryProviderLine = formatAssignmentLine(
    card.careTeam?.secondary?.occurredAt,
    card.careTeam?.secondary?.participant,
    "Not Assigned",
  );
  const lastEventLine = formatEventLine(card.lastEvent.occurredAt, card.lastEvent.title);
  const actionTypes = new Set(["Task", "Workflow", "Checklist", "Automation"]);
  const generalLinkedItems = card.linkedActions.items.filter((item) => !actionTypes.has(item.type));

  const tabButtonClass = (tabKey: RequestCardTabKey) =>
    cn(
      "relative flex h-full basis-1/3 items-center justify-center px-3 py-3 transition-colors",
      "md:basis-auto md:flex-1 md:w-full md:flex-col md:items-center md:px-3 md:py-6",
      activeTab === tabKey
        ? "bg-card text-primary shadow-sm"
        : "bg-transparent text-muted-foreground/50 hover:bg-muted/60 hover:text-muted-foreground",
    );

  const tabRailClass = cn(
    "flex overflow-hidden border-b border-border bg-muted/30 md:h-full md:w-40 md:flex-1 md:flex-shrink-0 md:flex-col md:border-b-0",
    activeTab ? "md:border-r-0" : "md:border-r",
  );

  return (
    <Card className="flex flex-col gap-0 min-h-[20rem] overflow-hidden border border-border/70 bg-card py-0 shadow-sm md:min-h-[24rem]">
      <div className="flex flex-1 flex-col md:grid md:grid-cols-[minmax(9rem,11rem)_1fr] md:grid-rows-1">
        <div className={tabRailClass}>
          <div className="flex divide-x divide-border md:h-full md:flex-1 md:flex-col md:divide-x-0 md:divide-y md:divide-border">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <Tooltip key={tab.key} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={tabButtonClass(tab.key)}
                      onClick={() => setActiveTab(tab.key)}
                      aria-label={tab.label}
                    >
                      <div className="flex h-full flex-col items-center justify-between gap-2">
                        <span className="flex flex-1 items-center justify-center">
                          <Icon
                            className={cn(
                              "h-8 w-8 transition-colors",
                              isActive ? "text-primary" : "text-muted-foreground/50",
                            )}
                          />
                        </span>
                        {tab.count && tab.count > 0 ? (
                          <span
                            className={cn(
                              "mb-4 rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                              isActive ? "bg-primary text-primary-foreground" : "bg-muted-foreground/20 text-muted-foreground",
                            )}
                          >
                            {tab.count}
                          </span>
                        ) : null}
                      </div>
                      {isActive ? (
                        <span
                          className="pointer-events-none absolute right-0 top-0 hidden h-full w-px bg-card md:block"
                          aria-hidden="true"
                        />
                      ) : null}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs font-semibold uppercase tracking-wide">
                    {tab.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
        <div className="flex flex-1 flex-col space-y-4 p-4">
          {activeTab === "overview" ? (
            <div className="flex h-full flex-col space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="text-lg font-semibold text-foreground">
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {renderLifecyclePath(card.typePath, {
                        mutedClass: "font-semibold text-foreground/70",
                        activeClass: "font-semibold text-foreground",
                        separatorClass: "text-muted-foreground",
                      })}
                    </span>
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                      statusMeta.className,
                    )}
                  >
                    <span className="inline-flex items-center text-current">{statusMeta.icon}</span>
                    <span className="inline-flex flex-wrap items-center">
                      {renderLifecyclePath(lifecyclePath)}
                    </span>
                  </span>
                </div>
                <Badge variant="outline" className="font-mono text-[11px]">
                  {card.id}
                </Badge>
              </div>
              <div className="flex flex-col gap-4">
                <div className="space-y-1">
                  <div className="text-xs font-semibold uppercase text-muted-foreground">Initiated</div>
                  <div className="flex flex-wrap items-center gap-1 text-sm text-foreground">
                    <span className="text-xs text-muted-foreground">{initiatedTimestamp}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="font-medium">{card.initiatedBy.name}</span>
                    {card.initiatedBy.role ? (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">{card.initiatedBy.role}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs font-semibold uppercase text-muted-foreground">Referred Patient</div>
                  <div className="flex flex-wrap items-center gap-1 text-sm text-foreground">
                    <span className="font-medium">{card.patient}</span>
                    {patientDobLabel ? (
                      <span className="text-xs text-muted-foreground">({patientDobLabel})</span>
                    ) : null}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs font-semibold uppercase text-muted-foreground">Description</div>
                  <div className="text-sm leading-relaxed text-foreground">{card.description}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs font-semibold uppercase text-muted-foreground">Flags</div>
                  {card.flags && card.flags.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-2">
                      {card.flags.map((flag) => (
                        <Badge key={flag} variant="outline" className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium">
                          <span>{flag}</span>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">No flags applied.</div>
                  )}
                </div>
              </div>
            </div>
          ) : activeTab === "timeline" ? (
            <div className="flex h-full flex-col space-y-4">
              <div className="space-y-4">
                {renderInfoRow("Current Assignee", currentAssigneeLine)}
                {renderInfoRow("Primary Provider", primaryProviderLine)}
                {renderInfoRow("Secondary Provider", secondaryProviderLine, "Not Assigned")}
                {renderInfoRow("Last Event", lastEventLine)}
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col space-y-4">
              <div className="space-y-4">
                {renderLinkedGroup("Conversations", card.linkedConversations.items, "No linked conversations.")}
                {renderLinkedGroup("Actions", card.linkedActions.items, "No linked actions.")}
                {renderLinkedGroup("Items", generalLinkedItems, "No additional linked items.")}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function RequestsTableView({ requests }: { requests: RequestCard[] }) {
  if (requests.length === 0) {
    return <RequestsEmptyState message="No requests match the current filters." />;
  }

  return (
    <Card>
      <CardHeader className="pb-0">
        <CardTitle className="text-base font-semibold text-foreground sm:text-lg">Requests Table</CardTitle>
        <CardDescription>Tabular overview of request statuses and routing.</CardDescription>
      </CardHeader>
      <CardContent className="px-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr className="border-b">
                <th className="px-4 py-2 text-left font-medium">ID / Type</th>
                <th className="px-4 py-2 text-left font-medium">Patient</th>
                <th className="px-4 py-2 text-left font-medium">Initiated</th>
                <th className="px-4 py-2 text-left font-medium">Assigned</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Last Event</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((card) => {
                const statusMeta = REQUEST_STATUS_META[card.quickFilterKey] ?? {
                  className: "bg-muted text-foreground border border-border",
                  icon: <Info className="size-3.5" />,
                };
                const lifecyclePath = card.lifecycle.path;
                return (
                  <tr key={card.id} className="border-b last:border-b-0">
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div className="font-mono text-xs text-muted-foreground">{card.id}</div>
                      <div className="mt-1 inline-flex flex-wrap items-center text-xs text-muted-foreground/80">
                        {renderLifecyclePath(card.typePath, {
                          mutedClass: "text-muted-foreground/80",
                          activeClass: "font-semibold text-muted-foreground",
                          separatorClass: "text-muted-foreground/50",
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{card.patient}</div>
                      <div className="text-xs text-muted-foreground">{card.requested}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div className="font-medium text-foreground">{card.initiatedBy.name}</div>
                      <div className="mt-1 text-muted-foreground/80">
                        {dateFormatter.format(new Date(card.initiatedAt))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div className="font-medium text-foreground">{card.assignee.name}</div>
                      {card.assignee.role ? (
                        <div className="mt-1 text-muted-foreground/80">{card.assignee.role}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                          statusMeta.className,
                        )}
                      >
                        <span className="inline-flex items-center text-current">{statusMeta.icon}</span>
                        <span className="inline-flex flex-wrap items-center">
                          {renderLifecyclePath(lifecyclePath)}
                        </span>
                      </span>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {dateFormatter.format(new Date(card.lifecycle.occurredAt))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-foreground">{card.lastEvent.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {dateFormatter.format(new Date(card.lastEvent.occurredAt))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function RequestsCardView({ requests }: { requests: RequestCard[] }) {
  if (requests.length === 0) {
    return <RequestsEmptyState message="No requests match the current filters." />;
  }

  return (
    <ScrollArea className="max-h-[70vh] pr-2">
      <div className="flex flex-col gap-4 pb-4">
        {requests.map((card) => (
          <RequestCardItem key={card.id} card={card} />
        ))}
      </div>
    </ScrollArea>
  );
}

export function FacilityRequestsView() {
  const { roleKey, roleConfig } = useRole();
  const isFacility = roleKey === "facilityNonMedical" || roleKey === "facilityMedical";
  const [viewMode, setViewMode] = useState<RequestViewMode>("cards");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterFavorite, setFilterFavorite] = useState<string>(REQUEST_FILTER_PLACEHOLDER);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [quickStatusGroup, setQuickStatusGroup] = useState<RequestQuickFilterGroupKey>("open");
  const [quickStatusKey, setQuickStatusKey] = useState<RequestQuickFilterKey | null>(null);
  const requestCards = useRequestCards();

  const activeFiltersCount = filterFavorite === REQUEST_FILTER_PLACEHOLDER ? 0 : 1;

  const handleResetFilters = useCallback(() => {
    setFilterFavorite(REQUEST_FILTER_PLACEHOLDER);
  }, []);

  const requestStatusCounts = useMemo(() => {
    return requestCards.reduce<Record<RequestQuickFilterKey, number>>((acc, card) => {
      acc[card.quickFilterKey] = (acc[card.quickFilterKey] ?? 0) + 1;
      return acc;
    }, {} as Record<RequestQuickFilterKey, number>);
  }, [requestCards]);

  const quickFilterCounts = useMemo<RequestQuickFilterCounts>(() => {
    const groupTotals: Record<RequestQuickFilterGroupKey, number> = { open: 0, closed: 0 };
    const chipCounts: Record<RequestQuickFilterGroupKey, Partial<Record<RequestQuickFilterKey, number>>> = {
      open: {},
      closed: {},
    };

    (Object.entries(REQUEST_QUICK_FILTERS) as Array<[
      RequestQuickFilterGroupKey,
      RequestQuickFilterDefinition,
    ]>).forEach(([groupKey, definition]) => {
      definition.chips.forEach((chip) => {
        const count = requestStatusCounts[chip.key] ?? 0;
        chipCounts[groupKey][chip.key] = count;
        groupTotals[groupKey] += count;
      });
    });

    return {
      groups: groupTotals,
      chips: chipCounts,
    };
  }, [requestStatusCounts]);

  const activeQuickFilterKeys = useMemo(() => {
    const definition = REQUEST_QUICK_FILTERS[quickStatusGroup];
    if (!definition) {
      return [] as RequestQuickFilterKey[];
    }
    if (quickStatusKey) {
      const chip = definition.chips.find((entry) => entry.key === quickStatusKey);
      return chip ? [chip.key] : [];
    }
    return definition.chips.map((chip) => chip.key);
  }, [quickStatusGroup, quickStatusKey]);

  const filteredRequests = useMemo(() => {
    const statuses = activeQuickFilterKeys;
    const query = searchQuery.trim().toLowerCase();
    return requestCards.filter((card) => {
      const matchesStatus = statuses.length === 0 || statuses.includes(card.quickFilterKey);
      if (!matchesStatus) {
        return false;
      }
      if (!query) {
        return true;
      }
      const haystack = [
        card.id,
        card.patient,
        card.requested,
        card.typePath.join(" "),
        card.description,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [activeQuickFilterKeys, requestCards, searchQuery]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleQuickStatusGroupChange = useCallback((group: RequestQuickFilterGroupKey) => {
    if (group === quickStatusGroup) {
      return;
    }
    setQuickStatusGroup(group);
    setQuickStatusKey(null);
  }, [quickStatusGroup]);

  const handleQuickStatusKeyChange = useCallback((key: RequestQuickFilterKey | null) => {
    setQuickStatusKey(key);
  }, []);

  return (
    <>
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="right" className="w-full sm:w-[360px]">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-3 text-sm text-muted-foreground">
            <p>Filter controls coming soon.</p>
          </div>
        </SheetContent>
      </Sheet>
      <Sheet open={newRequestOpen} onOpenChange={setNewRequestOpen}>
        <SheetContent side="right" className="w-full sm:w-[360px]">
          <SheetHeader>
            <SheetTitle>New Request</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-3 text-sm text-muted-foreground">
            <p>Request creation form coming soon.</p>
          </div>
        </SheetContent>
      </Sheet>
      <DashboardScaffold
        searchPlaceholder="Search requests or patients"
        header={
          <RequestsDashboardHeader
            viewMode={viewMode}
            onViewModeChange={(mode) => setViewMode(mode)}
            onOpenFilters={() => setFiltersOpen(true)}
            onResetFilters={handleResetFilters}
            filterFavorite={filterFavorite}
            onFilterFavoriteChange={(value) => setFilterFavorite(value)}
            activeFiltersCount={activeFiltersCount}
            onOpenNewRequest={() => setNewRequestOpen(true)}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            quickStatusGroup={quickStatusGroup}
            onQuickStatusGroupChange={handleQuickStatusGroupChange}
            quickStatusKey={quickStatusKey}
            onQuickStatusKeyChange={handleQuickStatusKeyChange}
            quickFilterCounts={quickFilterCounts}
          />
        }
      >
        {isFacility ? (
          viewMode === "cards" ? (
            <RequestsCardView requests={filteredRequests} />
          ) : (
            <RequestsTableView requests={filteredRequests} />
          )
        ) : (
          <RolePlaceholder title={`${roleConfig.label} · Requests`} />
        )}
      </DashboardScaffold>
    </>
  );
}

export function HomeDashboardView() {
  const { roleKey, roleConfig } = useRole();
  const isFacility = roleKey === "facilityNonMedical" || roleKey === "facilityMedical";

  if (!isFacility) {
    return (
      <DashboardScaffold searchPlaceholder="Search alerts or tasks">
        <RolePlaceholder title={`${roleConfig.label} · Home`} />
      </DashboardScaffold>
    );
  }

  return <HomeDashboard />;
}

function HomeDashboard() {
  return (
    <DashboardScaffold searchPlaceholder="Search alerts or tasks">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Home Dashboard Preview</CardTitle>
          <CardDescription>Interactive home widgets will appear here in future iterations.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Use the navigation on the left to explore other dashboards and workflows in the mock UI.
        </CardContent>
      </Card>
    </DashboardScaffold>
  );
}
